// The messaging engine: one XMPP connection per device, a local IndexedDB
// store, and the protocol glue between them.
//
//   XEP-0198 stream management  -> "sent" tick (server ack), resume
//   XEP-0184 delivery receipts  -> "delivered" tick
//   XEP-0333 chat markers       -> "read" (blue) ticks, read sync across devices
//   XEP-0085 chat states        -> "typing..."
//   XEP-0280 carbons            -> every device sees messages sent/received by the others
//   XEP-0313 MAM                -> history sync for new/reconnecting devices
//   XEP-0012 / XEP-0319         -> last seen / idle
//   XEP-0352 CSI                -> less traffic while the tab is in the background
//   XEP-0066 OOB + urn:xmm:media -> photos, video, voice notes, documents
//   XEP-0461 replies, XEP-0444 reactions, XEP-0308 edits, XEP-0424 delete for everyone
import { client, xml, type Client, type Element } from "@xmpp/client";
import { api, ApiError, isPrimary, type ChannelPost, type ChatSetting, type Group, type Privacy, type Session, type Star, type StatusItem } from "./api";
import {
  openStore,
  msgKey,
  messagesPage,
  messagesSince,
  summarize,
  Status,
  type Chat,
  type DB,
  type Kind,
  type LinkPreview,
  type Media,
  type Mention,
  type ContactCard,
  type Location,
  type Msg,
  type Quote,
  type ContactProfile,
  type Poll,
  type ChatEvent,
  type RSVP,
  type StickerRef,
} from "./db";
import * as S from "./state";
import { baseOrigin, baseURL, isNative, onPhone } from "./platform";
import { playSent } from "./sounds";
import type { E2E, Envelope } from "./e2e/e2e";
import { b64u, backupKey, exportHistory, exportPublic, importHistory, lastDays, linkKey, mergeBundles, newLinkKeys, seal, unseal, type Bundle } from "./history";
import { CallManager, NS_CALL, type CallMedia, type CallResult } from "./calls";
// The Signal library is large: load it as a separate chunk, in parallel.
const e2eModule = () => import("./e2e/e2e");
import { bare, uuid } from "./util";

const NS = {
  mam: "urn:xmpp:mam:2",
  rsm: "http://jabber.org/protocol/rsm",
  forward: "urn:xmpp:forward:0",
  delay: "urn:xmpp:delay",
  carbons: "urn:xmpp:carbons:2",
  receipts: "urn:xmpp:receipts",
  markers: "urn:xmpp:chat-markers:0",
  chatstates: "http://jabber.org/protocol/chatstates",
  sid: "urn:xmpp:sid:0",
  hints: "urn:xmpp:hints",
  roster: "jabber:iq:roster",
  last: "jabber:iq:last",
  idle: "urn:xmpp:idle:1",
  csi: "urn:xmpp:csi:0",
  profile: "urn:xmm:profile:0",
  forms: "jabber:x:data",
  oob: "jabber:x:oob",
  media: "urn:xmm:media:0",
  preview: "urn:xmm:preview:0",
  fwd: "urn:xmm:fwd:0",
  sync: "urn:xmm:sync:0",
  reply: "urn:xmpp:reply:0",
  reactions: "urn:xmpp:reactions:0",
  correct: "urn:xmpp:message-correct:0",
  retract: "urn:xmpp:message-retract:1",
  fallback: "urn:xmpp:fallback:0",
  group: "urn:xmm:group:0",
  pubsubEvent: "http://jabber.org/protocol/pubsub#event",
  reference: "urn:xmpp:reference:0",
  contact: "urn:xmm:contact:0",
  geoloc: "http://jabber.org/protocol/geoloc",
  live: "urn:xmm:live:0",
  self: "urn:xmm:self:0",
  e2e: "urn:xmm:e2e:0",
  blocking: "urn:xmpp:blocking",
  disappear: "urn:xmm:disappear:0",
  push: "urn:xmpp:push:0",
  status: "urn:xmm:status:0",
  channel: "urn:xmm:channel:0",
} as const;

const ALLOWED_TTLS = [0, 86400, 7 * 86400, 90 * 86400];

/** All messages from this client are end-to-end encrypted. */
const E2E_ON = true;
const E2E_FALLBACK = "🔒 This message is end-to-end encrypted.";

/** Live location: how often (at most) we send a position update. */
const LIVE_MIN_INTERVAL = 10_000;

/** How long after sending a message may still be edited / deleted for everyone. */
export const EDIT_WINDOW = 15 * 60_000;
export const RETRACT_WINDOW = 2 * 24 * 3_600_000;

/** What the UI hands the engine to send. */
export interface Draft {
  body: string;
  kind?: Kind;
  media?: Media;
  replyTo?: Quote;
  preview?: LinkPreview;
  forwarded?: boolean;
  mentions?: Mention[];
  contact?: ContactCard;
  location?: Location;
  viewOnce?: boolean;
  poll?: Poll;
  event?: ChatEvent;
  sticker?: StickerRef;
  statusRef?: Msg["statusRef"];
  /** sent by the app itself (no send sound) */
  quiet?: boolean;
}

/** Address-book names for people on xmm (the phone's, shared E2E), and the
 * contacts the sync saved (so it can remove them again). */
interface ContactBook {
  names: Record<string, string>;
  auto: string[];
  at: number;
  fp?: string; // fingerprint of the address book at the last sync (this phone only)
}

const PAGE = 50;
/** How much history a new device gets from another one. */
export const HISTORY_DAYS = 30;
/** A new device asks senders to re-send unreadable messages up to a day older
 * than itself (senders keep them that long, and only re-send the ones none of
 * our devices got). */
const RETRY_WINDOW = 24 * 3600_000;
/** Group history for new members: this many days, at most this many messages. */
const GROUP_HISTORY_DAYS = 14;
const GROUP_HISTORY_MAX = 100;
/** Changes reach the automatic history backup this long after they happen. */
const BACKUP_DELAY = 60_000;
const TYPING_TTL = 25_000;
const PROFILE_TTL = 3_600_000;

interface ArchivedItem {
  stanzaId: string;
  ts: number;
  message: Element;
}

interface Ctx {
  archived?: boolean;
  carbon?: "sent" | "received";
  stanzaId?: string;
  ts?: number;
}

export class Engine {
  readonly me: string;
  private xmpp!: Client;
  private chatMap = new Map<string, Chat>();
  private mam = new Map<string, ArchivedItem[]>();
  private queue: Promise<unknown> = Promise.resolve();
  private synced = false;
  private stopped = false;
  private composeTimers = new Map<string, number>(); // chat -> pause timer
  private idleSince = 0;
  private profileFetch = new Set<string>();
  private cleanup: (() => void)[] = [];

  private constructor(private session: Session, readonly db: DB) {
    this.me = session.xmpp.jid;
  }

  e2e!: E2E;
  calls!: CallManager;

  static async start(session: Session): Promise<Engine> {
    const db = await openStore(session.xmpp.jid);
    const e = new Engine(session, db);
    e.e2e = await (await e2eModule()).E2E.open(session.xmpp.jid);
    e.e2e.onIdentityChange = (jid) => void e.enqueue(() => e.securityNotice(jid));
    e.calls = new CallManager({
      me: e.me,
      full: e.me + "/" + session.xmpp.resource,
      deviceId: () => e.e2e.deviceId,
      online: () => e.xmpp?.status === "online",
      send: (el) => e.send(el),
      iq: (el) => e.xmpp.iqCaller.request(el),
      encryptFor: async (jid, device, payload) => envelopeEl(await e.e2e.encryptForDevice(jid, device, payload), "call"),
      decrypt: async (from, el) => {
        const env = parseEnvelope(el);
        if (!env) throw new Error("bad envelope");
        return e.e2e.decrypt(from, env);
      },
      logCall: (chat, sid, media, result, duration) => e.logCall(chat, sid, media, result, duration),
    });
    await e.loadLocal();
    // Closing the tab mid-call: best-effort hang-up so the other side isn't
    // left on "Reconnecting…".
    const onHide = () => void e.calls.hangup("Call ended");
    window.addEventListener("pagehide", onHide);
    e.cleanup.push(() => window.removeEventListener("pagehide", onHide));
    e.connect();
    e.watchVisibility();
    return e;
  }

  // ---- lifecycle ----------------------------------------------------------

  private async loadLocal() {
    S.mucDomain.value = this.session.xmpp.muc_domain;
    S.myJid.value = this.me;
    const groups = (await this.db.get("kv", "groups")) as Group[] | undefined;
    if (groups) S.groups.value = new Map(groups.map((g) => [g.jid, g]));
    for (const c of await this.db.getAll("chats")) this.chatMap.set(c.jid, c);
    this.publishChats();
    const profiles = new Map<string, ContactProfile>();
    for (const p of await this.db.getAll("profiles")) profiles.set(p.jid, p);
    S.profiles.value = profiles;
    const roster = (await this.db.get("kv", "roster")) as S.RosterItem[] | undefined;
    if (roster) S.roster.value = new Map(roster.map((r) => [r.jid, r]));
    const book = (await this.db.get("kv", "contactbook")) as ContactBook | undefined;
    if (book) S.contactNames.value = new Map(Object.entries(book.names));
    const freq = (await this.db.get("kv", "frequent")) as Record<string, { n: number; t: number }> | undefined;
    if (freq) this.publishFrequent(freq);
    const settings = (await this.db.get("kv", "chatSettings")) as ChatSetting[] | undefined;
    if (settings) S.chatSettings.value = new Map(settings.map((c) => [c.jid, c]));
    const privacy = (await this.db.get("kv", "privacy")) as Privacy | undefined;
    if (privacy) S.privacy.value = { ...S.privacy.value, ...privacy };
    const timers = (await this.db.get("kv", "timers")) as [string, number][] | undefined;
    if (timers) S.timers.value = new Map(timers);
    const blocked = (await this.db.get("kv", "blocked")) as string[] | undefined;
    if (blocked) S.blocked.value = new Set(blocked);
    const stars = (await this.db.get("kv", "stars")) as Star[] | undefined;
    if (stars) S.stars.value = new Set(stars.map((x) => msgKey(x.chat, x.id)));
  }

  private connect() {
    const info = this.session.xmpp;
    S.conn.value = "connecting";
    this.xmpp = client({
      service: wsURL(info.ws_url),
      domain: info.domain,
      resource: info.resource,
      // A fresh 5-minute token for every (re)connect. The endpoint checks the
      // device session, so a removed device cannot reconnect.
      credentials: async (authenticate) => {
        const t = await this.xmppToken();
        await authenticate({ username: info.jid.split("@")[0], password: t }, "PLAIN");
      },
    });
    const x = this.xmpp;
    x.on("status", (st) => {
      if (st === "online") S.conn.value = "online";
      else if (st !== "offline" || !this.stopped) S.conn.value = st === "offline" ? "offline" : "connecting";
    });
    x.on("error", (err) => {
      console.warn("[xmpp]", err.condition ?? err.message);
      if (err instanceof ApiError && err.status === 401) this.revoked();
    });
    x.on("online", () => void this.onOnline());
    x.on("stanza", (st) => this.onStanza(st));
    x.streamManagement.on("ack", (st) => {
      if (st.is("message") && st.getChild("body") && st.attrs.to && st.attrs.id)
        this.enqueue(() => this.applyStatus(bare(st.attrs.to), st.attrs.id!, Status.Sent));
    });
    x.iqCallee.set(NS.roster, "query", ({ stanza, element }) => {
      const from = stanza.attrs.from;
      if (from && bare(from) !== this.me) return false; // roster pushes only come from our server
      this.enqueue(() => this.onRosterPush(element));
      return true;
    });
    for (const op of ["block", "unblock"] as const)
      x.iqCallee.set(NS.blocking, op, ({ stanza, element }) => {
        const from = stanza.attrs.from;
        if (from && bare(from) !== this.me) return false;
        const jids = element.getChildren("item").map((i) => bare(i.attrs.jid));
        const set = new Set(op === "block" ? [...S.blocked.value, ...jids] : jids.length ? [...S.blocked.value].filter((j) => !jids.includes(j)) : []);
        S.blocked.value = set;
        void this.db.put("kv", [...set], "blocked");
        return true;
      });
    x.start().catch((e) => console.warn("[xmpp] start", e));
    this.sweeper = window.setInterval(() => void this.enqueue(() => this.sweepExpired()), 30_000);
  }

  private async xmppToken(): Promise<string> {
    try {
      return (await api.xmppToken()).token;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) this.revoked();
      throw e;
    }
  }

  private revoked() {
    if (this.stopped) return;
    void this.stop().then(() => api.forget());
  }

  private sweeper = 0;

  async stop() {
    await this.calls?.shutdown();
    this.stopped = true;
    clearInterval(this.sweeper);
    clearTimeout(this.backupTimer);
    clearTimeout(this.shareBookTimer);
    queueMicrotask(() => this.e2e?.close());
    for (const w of this.liveWatches.values()) {
      navigator.geolocation?.clearWatch(w.watch);
      clearInterval(w.timer);
    }
    this.cleanup.forEach((f) => f());
    try {
      this.xmpp.reconnect.stop();
      await this.xmpp.stop();
    } catch {
      /* already closed */
    }
    this.db.close();
    S.conn.value = "offline";
  }

  private async onOnline() {
    const x = this.xmpp;
    try {
      await x.iqCaller.request(xml("iq", { type: "set" }, xml("enable", { xmlns: NS.carbons })));
    } catch (e) {
      console.warn("carbons", e);
    }
    await this.fetchRoster();
    // Our public keys must be in the directory before anyone can write to us.
    await this.e2e.publish().catch((err) => console.warn("e2e publish", err));
    await this.loadGroups(); // before sync: group messages need member lists
    void this.loadChatSettings();
    void this.loadStars();
    void this.loadPrivacy();
    void this.loadTimers();
    void this.loadBlocklist();
    void this.loadChannels();
    void this.loadBroadcasts();
    void this.loadCommunities();
    void this.syncHistory();
    this.refreshStatusesSoon(0);
    void this.enablePush().catch((e) => console.warn("push", e));
    await this.sendPresence();
    if (!S.focused.value) await x.send(xml("inactive", { xmlns: NS.csi })).catch(() => {});
    await this.sync();
    await this.enqueue(() => this.resendPending());
    void this.resumeLive();
    const active = S.activeChat.value;
    if (active) {
      void this.queryLastSeen(active);
      void this.enqueue(() => this.markRead(active));
    }
  }

  private enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const p = this.queue.then(fn, fn);
    this.queue = p.catch((e) => console.error("[engine]", e));
    return p;
  }

  private send(el: Element) {
    if (this.xmpp.status !== "online") return Promise.resolve();
    return this.xmpp.send(el).catch((e) => console.warn("[xmpp] send", e));
  }

  // ---- presence & visibility ----------------------------------------------

  private watchVisibility() {
    let timer = 0;
    const update = () => {
      // Coming back to the window reads what arrived while you were away.
      const looking = S.isAttentive();
      if (looking !== S.attentive.value) {
        S.attentive.value = looking;
        const chat = S.activeChat.value;
        if (looking && chat) void this.enqueue(() => this.markRead(chat));
      }
      const now = S.isForeground();
      if (now === S.focused.value) return;
      S.focused.value = now;
      if (!now) this.idleSince = Date.now();
      clearTimeout(timer);
      // Debounce so quick alt-tabs do not flap "online".
      timer = window.setTimeout(() => {
        void this.sendPresence();
        void this.send(xml(S.focused.value ? "active" : "inactive", { xmlns: NS.csi }));
      }, now ? 0 : 5000);
    };
    for (const ev of ["visibilitychange", "focus", "blur"]) {
      const target = ev === "visibilitychange" ? document : window;
      target.addEventListener(ev, update);
      this.cleanup.push(() => target.removeEventListener(ev, update));
    }
  }

  private sendPresence() {
    if (S.focused.value) return this.send(xml("presence", {}, xml("priority", {}, "10")));
    return this.send(
      xml(
        "presence",
        {},
        xml("show", {}, "away"),
        xml("priority", {}, "5"),
        xml("idle", { xmlns: NS.idle, since: new Date(this.idleSince || Date.now()).toISOString() }),
      ),
    );
  }

  private onPresence(p: Element) {
    const from = p.attrs.from ?? "";
    const jid = bare(from);
    const resource = from.slice(jid.length + 1);
    if (!jid || jid === this.me) return;
    const type = p.attrs.type;
    if (type && type !== "unavailable") return; // subscriptions are managed by the backend
    const map = new Map(S.presence.value);
    const cur: S.PeerPresence = { resources: new Map(map.get(jid)?.resources), lastActive: map.get(jid)?.lastActive };
    if (type === "unavailable") {
      cur.resources.delete(resource);
      if (!cur.resources.size) {
        cur.lastActive = Date.now();
        S.lastSeen.value = new Map(S.lastSeen.value).set(jid, Date.now());
      }
    } else {
      const show = p.getChildText("show");
      const away = show === "away" || show === "xa";
      cur.resources.set(resource, away);
      const since = p.getChild("idle", NS.idle)?.attrs.since;
      if (away && since) cur.lastActive = Date.parse(since);
    }
    map.set(jid, cur);
    S.presence.value = map;
  }

  async queryLastSeen(jid: string) {
    if (!this.isUser(jid) || S.isOnline(jid) || this.xmpp.status !== "online") return;
    const idle = S.presence.value.get(jid);
    if (idle?.resources.size && idle.lastActive) {
      S.lastSeen.value = new Map(S.lastSeen.value).set(jid, idle.lastActive);
      return;
    }
    try {
      const res = await this.xmpp.iqCaller.request(xml("iq", { type: "get", to: jid }, xml("query", { xmlns: NS.last })));
      const secs = Number(res.getChild("query", NS.last)?.attrs.seconds);
      if (Number.isFinite(secs)) S.lastSeen.value = new Map(S.lastSeen.value).set(jid, Date.now() - secs * 1000);
    } catch {
      S.lastSeen.value = new Map(S.lastSeen.value).set(jid, null); // hidden or unknown
    }
  }

  // ---- roster & profiles --------------------------------------------------

  private async fetchRoster() {
    try {
      const res = await this.xmpp.iqCaller.request(xml("iq", { type: "get" }, xml("query", { xmlns: NS.roster })));
      const map = new Map<string, S.RosterItem>();
      for (const it of res.getChild("query", NS.roster)?.getChildren("item") ?? []) {
        const r = rosterItem(it);
        map.set(r.jid, r);
      }
      S.roster.value = map;
      await this.db.put("kv", [...map.values()], "roster");
      void this.ensureProfiles([...map.keys(), ...this.chatMap.keys()]);
    } catch (e) {
      console.warn("roster", e);
    }
  }

  private async onRosterPush(q: Element) {
    const map = new Map(S.roster.value);
    let reannounce = false;
    for (const it of q.getChildren("item")) {
      const r = rosterItem(it);
      const prev = map.get(r.jid);
      if (r.subscription === "remove") map.delete(r.jid);
      else map.set(r.jid, r);
      const sharesNow = r.subscription === "from" || r.subscription === "both";
      const sharedBefore = prev?.subscription === "from" || prev?.subscription === "both";
      if (sharesNow && !sharedBefore) reannounce = true;
    }
    S.roster.value = map;
    await this.db.put("kv", [...map.values()], "roster");
    // Someone just added us: re-broadcast presence so they see us online.
    if (reannounce) await this.sendPresence();
    void this.ensureProfiles([...map.keys()]);
  }

  async ensureProfiles(jids: string[], force = false) {
    const now = Date.now();
    const need = [...new Set(jids)].filter((j) => {
      if (!j || !this.isUser(j) || this.profileFetch.has(j)) return false;
      const p = S.profiles.value.get(j);
      return force || !p || now - p.fetchedAt > PROFILE_TTL;
    });
    if (!need.length) return;
    need.forEach((j) => this.profileFetch.add(j));
    try {
      for (let i = 0; i < need.length; i += 200) {
        const { users } = await api.users(need.slice(i, i + 200));
        const map = new Map(S.profiles.value);
        const tx = this.db.transaction("profiles", "readwrite");
        for (const u of users) {
          const p = { ...u, fetchedAt: now };
          map.set(u.jid, p);
          void tx.store.put(p);
        }
        await tx.done;
        S.profiles.value = map;
      }
    } catch (e) {
      console.warn("profiles", e);
    } finally {
      need.forEach((j) => this.profileFetch.delete(j));
    }
  }

  // ---- incoming stanzas ---------------------------------------------------

  private onStanza(st: Element) {
    if (st.is("presence")) return this.onPresence(st);
    if (!st.is("message")) return;

    const result = st.getChild("result", NS.mam);
    if (result) {
      const items = this.mam.get(result.attrs.queryid ?? "");
      const from = st.attrs.from;
      if (!items || (from && bare(from) !== this.me)) return;
      const fwd = result.getChild("forwarded", NS.forward);
      const inner = fwd?.getChild("message");
      if (!fwd || !inner || !result.attrs.id) return;
      const stamp = fwd.getChild("delay", NS.delay)?.attrs.stamp;
      items.push({ stanzaId: result.attrs.id, ts: stamp ? Date.parse(stamp) : Date.now(), message: inner });
      return;
    }

    for (const kind of ["sent", "received"] as const) {
      const c = st.getChild(kind, NS.carbons);
      if (!c) continue;
      if (bare(st.attrs.from) !== this.me) return; // forged carbon
      const inner = c.getChild("forwarded", NS.forward)?.getChild("message");
      if (inner) this.enqueue(() => this.route(inner, { carbon: kind }));
      return;
    }
    this.enqueue(() => this.route(st, {}));
  }

  /** Dispatch to the 1:1 or group handler. Group messages reach us through
   * MUC/Sub as pubsub events from the room (live, offline and in our own
   * archive), so every device gets them without joining the room. */
  private async route(m: Element, ctx: Ctx) {
    // Track our archive position from live traffic once caught up.
    if (!ctx.archived && this.synced) {
      const sid = m.getChildren("stanza-id", NS.sid).find((s) => s.attrs.by === this.me)?.attrs.id;
      if (sid) void this.db.put("kv", sid, "mam:cursor");
    }
    const from = bare(m.attrs.from);
    if (from && (S.isGroup(from) || S.isChannel(from))) {
      if (m.attrs.type === "error") {
        const id = m.attrs.id;
        if (id) await this.applyStatus(from, id, Status.Failed);
        return;
      }
      const items = m.getChild("event", NS.pubsubEvent)?.getChild("items");
      if (items) {
        if (items.attrs.node !== "urn:xmpp:mucsub:nodes:messages") return;
        for (const it of items.getChildren("item")) {
          const inner = it.getChild("message");
          if (inner) await this.handleGroup(inner, ctx);
        }
        return;
      }
      if (m.attrs.type === "groupchat") return this.handleGroup(m, ctx);
      return;
    }
    return this.handleMessage(m, ctx);
  }

  private async handleMessage(m: Element, ctx: Ctx) {
    const type = m.attrs.type ?? "normal";
    const from = bare(m.attrs.from) || this.me;
    const to = bare(m.attrs.to) || this.me;

    const prof = m.getChild("profile", NS.profile);
    if (prof && !ctx.archived) {
      if (prof.attrs.jid) void this.ensureProfiles([prof.attrs.jid], true);
      return;
    }
    // A device wants to link by code (only from our own account: the server).
    const lc = m.getChild("link-code", "urn:xmm:link:0");
    if (lc && !ctx.archived) {
      if (from === this.me) S.linkCodeRequest.value = { device: (lc.attrs.device ?? "").slice(0, 80), at: Date.now() };
      return;
    }
    const nudge = m.getChild("sync", NS.sync);
    // "devices" nudges come from the contact whose devices changed; they only
    // drop a cache entry, so they're harmless from anyone.
    if (nudge && nudge.attrs.what === "devices" && !ctx.archived) {
      if (nudge.attrs.jid) this.e2e.forget(bare(nudge.attrs.jid));
      // One of our devices was added (published its keys): the phone shares
      // the address-book names with it.
      if (isPrimary() && nudge.attrs.jid && bare(nudge.attrs.jid) === this.me && from === this.me) {
        clearTimeout(this.shareBookTimer);
        this.shareBookTimer = window.setTimeout(() => void this.shareContactBook(), 2000);
      }
      return;
    }
    // Status updates: a contact posted/deleted one, or someone viewed ours.
    // Only triggers a refetch, so it's harmless from anyone.
    const stn = m.getChild("status", NS.status);
    if (stn && !ctx.archived) {
      this.refreshStatusesSoon();
      return;
    }
    if (nudge && from === this.me && !ctx.archived) {
      const what = nudge.attrs.what;
      if (what === "stars") void this.loadStars();
      else if (what === "privacy") void this.loadPrivacy();
      else if (what === "status" || what === "status-privacy") this.refreshStatusesSoon();
      else if (what === "channels") void this.loadChannels();
      else if (what === "broadcasts") void this.loadBroadcasts();
      else if (what === "history" || what === "devices") void this.syncHistory();
      else if (what === "group-requests") S.joinRequestsChanged.value++;
      else void this.loadChatSettings();
      return;
    }
    const ghReq = m.getChild("history-request", NS.group);
    if (ghReq?.attrs.room && from !== this.me) {
      if (Date.now() - (ctx.ts ?? Date.now()) < 24 * 3600_000) void this.answerGroupHistoryRequest(from, ghReq.attrs.room);
      return;
    }
    const retry = m.getChild("retry", NS.e2e);
    if (retry) {
      // Live or caught up from the archive (sender was offline), within a day.
      const at = ctx.ts ?? Date.now();
      if (Date.now() - at < 24 * 3600_000) return this.onRetryRequest(from, retry);
      return;
    }
    if (type === "groupchat" || type === "headline") return;

    const out = from === this.me;
    const chat = out ? to : from;
    // Group receipts travel 1:1 (member -> author) and name the group.
    const groupRef = m.getChild("group", NS.group)?.attrs.jid;
    if (groupRef) {
      if (S.isGroup(groupRef)) await this.onGroupReceipt(m, groupRef, from, out);
      return;
    }
    // Disappearing-messages timer changed (by us on another device, or them).
    const dis = m.getChild("disappearing", NS.disappear);
    if (dis) {
      const ttl = Number(dis.attrs.ttl);
      const cid = m.attrs.id;
      if (ALLOWED_TTLS.includes(ttl) && cid) {
        S.timers.value = new Map(S.timers.value).set(chat, ttl);
        void this.db.put("kv", [...S.timers.value], "timers");
        await this.systemNotice(chat, "dis-" + cid, "disappearing", from, String(ttl), ctx.ts);
      }
      return;
    }
    // Read marker we sent only to our own devices (read receipts off), or a
    // view-once photo/video we opened on another device.
    const selfRef = m.getChild("chat", NS.self)?.attrs.jid;
    if (selfRef) {
      const disp = m.getChild("displayed", NS.markers)?.attrs.id;
      if (out && to === this.me && disp) await this.readUpTo(selfRef, disp);
      const viewed = m.getChild("viewed", NS.self)?.attrs.id;
      if (out && to === this.me && viewed) await this.wipeViewOnce(msgKey(selfRef, viewed));
      return;
    }
    if (!chat.includes("@") || S.isGroup(chat) || S.isChannel(chat)) return;
    const id = m.getChild("origin-id", NS.sid)?.attrs.id ?? m.attrs.id;

    if (type === "error") {
      if (id && !out) await this.applyStatus(chat, id, Status.Failed);
      return;
    }

    const rcv = m.getChild("received", NS.receipts);
    if (rcv?.attrs.id && !out) await this.applyStatus(chat, rcv.attrs.id, Status.Delivered);

    const disp = m.getChild("displayed", NS.markers);
    if (disp?.attrs.id) {
      if (out) await this.readUpTo(chat, disp.attrs.id); // we read it on another device
      else await this.applyStatus(chat, disp.attrs.id, Status.Read);
    }

    if (!out && !ctx.archived) {
      const composing = m.getChild("composing", NS.chatstates);
      const stopped = ["paused", "active", "inactive", "gone"].some((n) => m.getChild(n, NS.chatstates));
      if (composing) this.setTyping(chat, Date.now() + TYPING_TTL);
      else if (stopped) this.setTyping(chat, 0);
    }

    const stamp = m.getChild("delay", NS.delay)?.attrs.stamp;
    let ts = ctx.ts ?? (stamp ? Date.parse(stamp) : Date.now());

    // Calls: ringing (XEP-0353) and the encrypted SDP for this device.
    if (this.calls.onJingleMessage(m, ctx, ts)) return;
    const callSig = m.getChild("encrypted", NS.e2e);
    if (callSig?.attrs.op === "call") {
      if (!ctx.archived && !ctx.carbon && m.attrs.from) await this.calls.onSignal(m.attrs.from, callSig);
      return;
    }

    // Operations on an earlier message. They carry a fallback <body> for
    // other clients, so they must be handled before normal messages.
    if (await this.applyLiveOps(chat, from, m, ts)) return;
    const retract = m.getChild("retract", NS.retract);
    if (retract?.attrs.id) return this.applyRetract(chat, from, retract.attrs.id);
    const replace = m.getChild("replace", NS.correct);
    if (replace?.attrs.id) return this.applyEdit(chat, from, replace.attrs.id, m, ts);
    const reactions = m.getChild("reactions", NS.reactions);
    if (reactions?.attrs.id) {
      const emoji = reactions.getChildren("reaction").map((r) => r.getText()).find((e) => e && e.length <= 16);
      return this.applyReaction(chat, from, reactions.attrs.id, emoji);
    }

    let content: Content | undefined;
    const encEl = m.getChild("encrypted", NS.e2e);
    if (encEl) {
      // Ask only about recent messages; the sender enforces the real rule
      // (sent after this device's session began, or never delivered to any
      // of our devices), so a new device never gets older history this way.
      const r = await this.openEncrypted(chat, from, id, encEl, ts, !out && ts >= this.e2e.createdAt - RETRY_WINDOW);
      if (r.done) return;
      content = r.content;
      if (this.skipOldUnreadable(content, ts)) return;
      if (r.sent) ts = r.sent; // the sender's order, not arrival order
    }
    const clog = m.getChild("call-log", NS_CALL);
    if (clog) content = callContent(clog, false);
    const body = m.getChildText("body");
    if ((!content && body === null) || !id) return;
    if (!out) this.setTyping(chat, 0);

    const msg: Msg = {
      key: msgKey(chat, id),
      id,
      chat,
      from,
      dir: out ? "out" : "in",
      ...(content ?? { ...parseContent(m, body!), insecure: E2E_ON || undefined }),
      ts,
      status: out ? Status.Sent : Status.Pending,
      stanzaId: ctx.stanzaId ?? m.getChildren("stanza-id", NS.sid).find((s) => s.attrs.by === this.me)?.attrs.id,
    };
    if (msg.replyTo) msg.replyTo = await this.snapshotQuote(chat, msg.replyTo);
    if (msg.ttl) msg.expiresAt = msg.ts + msg.ttl * 1000;
    if (!out) delete msg.bcast; // only meaningful for our own copies
    if (!(await this.insertMessage(msg))) return;
    if (out && msg.bcast && S.isBroadcast(msg.bcast)) {
      // Sent to a broadcast list from another of our devices: show it there too.
      const copy: Msg = { ...msg, key: msgKey(msg.bcast, msg.id), chat: msg.bcast };
      if (await this.insertMessage(copy)) this.touchChat(copy, false);
    }

    const live = !ctx.archived;
    // No receipt for a message we can't read: to the sender it stays
    // undelivered, so it re-sends it when we ask.
    if (!out && live && !msg.undecryptable && ctx.carbon !== "received" && m.getChild("request", NS.receipts)) {
      void this.send(
        xml("message", { to: chat, type: "chat", id: uuid() }, xml("received", { xmlns: NS.receipts, id }), xml("store", { xmlns: NS.hints })),
      );
    }
    const viewing = this.isViewing(chat);
    // Calls we answered don't count as unread; missed ones do.
    const quiet = msg.kind === "call" && msg.call?.result === "answered";
    this.touchChat(msg, !out && !viewing && !quiet);
    if (!out && viewing && live) await this.markRead(chat, msg);
    if (!out && !S.roster.value.has(chat)) void this.ensureProfiles([chat]);
    if (!out && live && !viewing && !quiet) this.notify(msg);
  }

  // ---- groups ------------------------------------------------------------------

  private async handleGroup(m: Element, ctx: Ctx) {
    const full = m.attrs.from ?? "";
    const room = bare(full);
    const nick = full.slice(room.length + 1);
    if (!nick) return; // room-level messages (subject etc.)
    const id = m.getChild("origin-id", NS.sid)?.attrs.id ?? m.attrs.id;
    const stamp = m.getChild("delay", NS.delay)?.attrs.stamp;
    let ts = ctx.ts ?? (stamp ? Date.parse(stamp) : Date.now());
    const stanzaId = ctx.stanzaId ?? m.getChildren("stanza-id", NS.sid).find((s) => s.attrs.by === room)?.attrs.id;

    // Only the server's system account (nick reserved in every room) may
    // post group events.
    if (S.isChannel(room)) return this.handleChannel(room, nick, m, id, ts, stanzaId, ctx);
    if (nick === "system") return this.onGroupEvent(room, m, id, ts, stanzaId, ctx);

    // Nicks are the members' phone localparts; ejabberd refuses a nick that
    // belongs to another subscriber, so this identifies the sender.
    const from = nick + "@" + this.session.xmpp.domain;
    const out = from === this.me;

    if (!out && !ctx.archived) {
      if (m.getChild("composing", NS.chatstates)) this.setTyping(room, Date.now() + TYPING_TTL, from);
      else if (["paused", "active", "inactive", "gone"].some((n) => m.getChild(n, NS.chatstates))) this.setTyping(room, 0);
    }

    // Group calls: join / leave / end, and the invite (also a chat entry).
    const callCtl = m.children.find((c): c is Element => typeof c !== "string" && c.attrs.xmlns === NS_CALL);
    if (callCtl && callCtl.name !== "call-invite") {
      if (callCtl.name === "call-end") await this.applyGroupCallEnd(room, callCtl);
      this.calls.onGroupControl(room, from, callCtl, ctx, ts);
      return;
    }
    if (callCtl) this.calls.onGroupControl(room, from, callCtl, ctx, ts);

    if (await this.applyLiveOps(room, from, m, ts)) return;
    const retract = m.getChild("retract", NS.retract);
    if (retract?.attrs.id) return this.applyRetract(room, from, retract.attrs.id);
    const replace = m.getChild("replace", NS.correct);
    if (replace?.attrs.id) return this.applyEdit(room, from, replace.attrs.id, m, ts);
    const reactions = m.getChild("reactions", NS.reactions);
    if (reactions?.attrs.id) {
      const emoji = reactions.getChildren("reaction").map((r) => r.getText()).find((e) => e && e.length <= 16);
      return this.applyReaction(room, from, reactions.attrs.id, emoji);
    }

    let content: Content | undefined;
    const encEl = m.getChild("encrypted", NS.e2e);
    if (encEl) {
      const seen = id ? await this.db.get("messages", msgKey(room, id)) : undefined;
      // A copy we couldn't read may come again, encrypted for us (catch-up).
      if (seen && !(seen.undecryptable && !out)) {
        if (out) await this.applyStatus(room, id!, Status.Sent); // our own echo
        return;
      }
      // Undecryptable group messages ask the sender for a copy too (below).
      const r = await this.openEncrypted(room, from, id, encEl, ts, !out && ts >= this.e2e.createdAt - RETRY_WINDOW);
      if (r.done) return;
      content = r.content;
      if (this.skipOldUnreadable(content, ts)) return;
      if (r.sent) ts = r.sent; // the sender's order, not arrival order
    }
    if (callCtl) content = callContent(callCtl, true);
    const body = m.getChildText("body");
    if ((!content && body === null) || !id) return;
    if (!out) this.setTyping(room, 0);

    // "Only admins can send": the server can't enforce this for MUC/Sub
    // subscribers, so every client ignores non-admin posts made after the
    // setting was switched on.
    const g = S.groups.value.get(room);
    if (g?.only_admins_send && g.admins_send_since && ts > g.admins_send_since * 1000 && !S.isGroupAdmin(room, from)) return;

    const msg: Msg = {
      key: msgKey(room, id),
      id,
      chat: room,
      from,
      dir: out ? "out" : "in",
      ...(content ?? { ...parseContent(m, body!), insecure: E2E_ON || undefined }),
      ts,
      status: out ? Status.Sent : Status.Pending,
      stanzaId,
    };
    if (msg.replyTo) msg.replyTo = await this.snapshotQuote(room, msg.replyTo);
    if (msg.ttl && !msg.kept) msg.expiresAt = msg.ts + msg.ttl * 1000;
    if (!(await this.insertMessage(msg))) {
      if (out) await this.applyStatus(room, id, Status.Sent); // our own echo
      return;
    }

    const live = !ctx.archived;
    if (!out && live && !msg.undecryptable && m.getChild("request", NS.receipts)) {
      void this.send(
        xml(
          "message",
          { to: from, type: "chat", id: uuid() },
          xml("received", { xmlns: NS.receipts, id }),
          xml("group", { xmlns: NS.group, jid: room }),
          xml("store", { xmlns: NS.hints }),
        ),
      );
    }
    const viewing = this.isViewing(room);
    // "@everyone" is a mention of the group itself.
    const mentioned = !out && !!msg.mentions?.some((x) => x.jid === this.me || x.jid === room);
    this.touchChat(msg, !out && !viewing, mentioned && !viewing);
    if (!out && viewing && live) await this.markRead(room, msg);
    if (!out && !S.profiles.value.has(from)) void this.ensureProfiles([from]);
    if (!out && live && !viewing) this.notify(msg, mentioned);
  }

  private async onGroupEvent(room: string, m: Element, id: string | undefined, ts: number, stanzaId: string | undefined, ctx: Ctx) {
    const x = m.getChild("x", NS.group);
    if (!x || !id) return;
    const system = {
      type: x.attrs.type ?? "",
      actor: x.attrs.actor ?? "",
      targets: (x.attrs.targets ?? "").split(",").filter(Boolean),
      value: x.attrs.value ?? "",
    };
    const msg: Msg = {
      key: msgKey(room, id),
      id,
      chat: room,
      from: "system",
      dir: "in",
      kind: "system",
      body: m.getChildText("body") ?? "",
      ts,
      status: Status.Pending,
      stanzaId,
      system,
    };
    if (!(await this.insertMessage(msg))) return;
    this.touchChat(msg, false);
    if (!ctx.archived) this.scheduleGroupRefresh(room);
    void this.ensureProfiles([system.actor, ...system.targets].filter((j) => j.includes("@")));
  }

  private refreshTimers = new Map<string, number>();
  private scheduleGroupRefresh(room: string) {
    clearTimeout(this.refreshTimers.get(room));
    this.refreshTimers.set(room, window.setTimeout(() => void this.loadGroup(room), 250));
  }

  private async onGroupReceipt(m: Element, room: string, from: string, out: boolean) {
    const rcv = m.getChild("received", NS.receipts)?.attrs.id;
    const disp = m.getChild("displayed", NS.markers)?.attrs.id;
    if (out) {
      // Our own read marker, sent from another device.
      if (disp) await this.readUpTo(room, disp);
      return;
    }
    if (rcv) await this.applyGroupReceipt(room, rcv, from, "d");
    if (disp) await this.applyGroupReceipt(room, disp, from, "r");
  }

  /** Record that `member` got (d) or read (r) our message; a read marker
   * covers all our earlier messages in the group. */
  private async applyGroupReceipt(room: string, id: string, member: string, kind: "d" | "r") {
    const target = await this.db.get("messages", msgKey(room, id));
    if (!target || target.dir !== "out") return;
    const now = Date.now();
    const mark = (m: Msg): Msg | null => {
      const prev = m.receipts?.[member] ?? {};
      if (kind === "r" && prev.r) return null;
      if (kind === "d" && (prev.d || prev.r)) return null;
      const next = kind === "r" ? { d: prev.d ?? now, r: now } : { ...prev, d: now };
      const receipts = { ...m.receipts, [member]: next };
      const status = Math.max(m.status, this.groupStatus({ ...m, receipts })) as Status;
      return { ...m, receipts, status };
    };
    const changed: Msg[] = [];
    const first = mark(target);
    if (first) changed.push(first);
    if (kind === "r") {
      const range = IDBKeyRange.bound([room, -Infinity], [room, target.ts], false, true);
      let cur = await this.db.transaction("messages").store.index("byChatTs").openCursor(range, "prev");
      while (cur) {
        const m = cur.value;
        if (m.dir === "out") {
          if (m.receipts?.[member]?.r) break;
          const next = mark(m);
          if (next) changed.push(next);
        }
        cur = await cur.continue();
      }
    }
    if (!changed.length) return;
    if (!changed.length) return;
    const tx = this.db.transaction("messages", "readwrite");
    changed.forEach((m) => void tx.store.put(m));
    await tx.done;
    if (S.activeChat.value === room) {
      const byKey = new Map(changed.map((m) => [m.key, m]));
      S.messages.value = S.messages.value.map((m) => byKey.get(m.key) ?? m);
    }
    const c = this.chatMap.get(room);
    const last = c && changed.find((m) => m.id === c.lastId);
    if (c && last) this.saveChat({ ...c, lastStatus: last.status });
  }

  /** Blue ticks when every member (present when it was sent) has read it. */
  groupStatus(m: Msg): Status {
    const g = S.groups.value.get(m.chat);
    const members = g?.members.filter((x) => x.jid !== this.me && x.joined_at * 1000 <= m.ts + 60_000) ?? [];
    if (!members.length) return m.status;
    const rc = m.receipts ?? {};
    if (members.every((x) => rc[x.jid]?.r)) return Status.Read;
    if (members.every((x) => rc[x.jid]?.d || rc[x.jid]?.r)) return Status.Delivered;
    return Math.min(m.status, Status.Sent) as Status;
  }

  async loadGroups() {
    try {
      const { groups } = await api.groups();
      const map = new Map(groups.map((g) => [g.jid, g]));
      // Groups we have chats for but are no longer in.
      for (const jid of this.chatMap.keys()) {
        if (S.isGroup(jid) && !map.has(jid)) map.set(jid, { ...(S.groups.value.get(jid) ?? blankGroup(jid)), left: true, my_role: "" });
      }
      S.groups.value = map;
      await this.db.put("kv", [...map.values()], "groups");
      for (const g of groups) this.ensureGroupChat(g);
      void this.ensureProfiles(groups.flatMap((g) => g.members.map((m) => m.jid)));
    } catch (e) {
      console.warn("groups", e);
    }
  }

  async loadGroup(jid: string) {
    try {
      this.applyGroup(await api.group(jid));
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        const old = S.groups.value.get(jid) ?? blankGroup(jid);
        this.applyGroup({ ...old, left: true, my_role: "" });
      }
    }
  }

  /** Store fresh group info (from an API response). */
  applyGroup(g: Group) {
    S.groups.value = new Map(S.groups.value).set(g.jid, g);
    void this.db.put("kv", [...S.groups.value.values()], "groups");
    this.ensureGroupChat(g);
    void this.ensureProfiles(g.members.map((m) => m.jid));
  }

  private ensureGroupChat(g: Group) {
    if (g.left || this.chatMap.has(g.jid)) return;
    this.saveChat({ jid: g.jid, lastBody: "", lastTs: g.created_at * 1000, lastDir: "", lastStatus: Status.Pending, unread: 0 });
  }

  async createGroup(name: string, members: string[], avatarMediaId?: string) {
    const g = await api.createGroup({ name, members, avatar_media_id: avatarMediaId });
    this.applyGroup(g);
    if (g.skipped?.length)
      window.dispatchEvent(
        new CustomEvent("xmm-toast", { detail: `${g.skipped.map((j) => S.displayName(j)).join(", ")} couldn't be added because of their privacy settings. Send them an invite link.` }),
      );
    await this.openChat(g.jid);
    return g;
  }

  // ---- operations on existing messages --------------------------------------

  private async updateMessage(key: string, fn: (m: Msg) => Msg | null) {
    const cur = await this.db.get("messages", key);
    if (!cur) return null;
    const next = fn(cur);
    if (!next) return null;
    await this.db.put("messages", next);
    this.markBackupDirty();
    if (S.activeChat.value === next.chat) S.messages.value = S.messages.value.map((x) => (x.key === key ? next : x));
    const c = this.chatMap.get(next.chat);
    if (c?.lastId === next.id) this.saveChat({ ...c, lastBody: summarize(next), lastDir: next.dir });
    return next;
  }

  // ---- end-to-end encryption -----------------------------------------------------

  /** Decrypt an encrypted stanza. Edits / live updates are applied here
   * (`done`); a new message returns its content (or an "undecryptable"
   * placeholder when it wasn't encrypted for this device). */
  private async openEncrypted(
    chat: string,
    from: string,
    id: string | undefined,
    el: Element,
    ts: number,
    canRetry = false,
  ): Promise<{ done: true } | { done: false; content: Content; sent?: number }> {
    const op = el.attrs.op ?? "msg";
    // Never decrypt twice: a Double Ratchet message key works only once. The
    // exception is a re-send (retry receipt) of a message we couldn't read.
    const prev = op === "msg" && id ? await this.db.get("messages", msgKey(chat, id)) : undefined;
    if (op === "msg" && (!id || (prev && !(prev.undecryptable && prev.from === from)))) return { done: true };
    const env = parseEnvelope(el);
    let payload: Record<string, unknown> | null = null;
    try {
      if (env) payload = (await this.e2e.decrypt(from, env)) as Record<string, unknown>;
    } catch (e) {
      console.warn("decrypt", from, (e as Error).message);
    }
    if (!payload || typeof payload !== "object") {
      if (op !== "msg" || prev) return { done: true };
      // Probably sent while the sender's copy of our device list was stale
      // (e.g. we were linked seconds ago): ask them to encrypt it again.
      if (canRetry && env && id) void this.requestRetry(from, id, S.isGroup(chat) ? chat : undefined);
      return { done: false, content: { kind: "text", body: "", undecryptable: true } as Content };
    }
    if ((payload.op === "vote" || payload.op === "rsvp" || payload.op === "event-cancel" || payload.op === "pin" || payload.op === "keep") && typeof payload.id === "string") {
      await this.applyResponse(chat, msgKey(chat, payload.id.slice(0, 128)), { op: payload.op, from, ts, choices: payload.choices, answer: payload.answer, until: numv(payload.until), kept: payload.kept === true });
      return { done: true };
    }
    if (payload.op === "contacts") {
      // Our phone's address-book names (only ever from our own devices).
      if (from === this.me) await this.applyContactBook(payload);
      return { done: true };
    }
    if (payload.op === "ghist") {
      // A group's recent messages, shared with us by the admin who added us.
      if (!S.isGroup(chat) && from !== this.me) void this.receiveGroupHistory(from, payload);
      return { done: true };
    }
    if (prev) {
      if (payload.op === "msg") {
        const content = sanitizeContent(payload);
        await this.updateMessage(prev.key, (x) => ({ ...x, ...content, ts: sentTime(payload!.sent, x.ts), undecryptable: undefined }));
        if (from !== this.me) {
          const group = S.isGroup(chat);
          void this.send(
            xml(
              "message",
              { to: from, type: "chat", id: uuid() },
              xml("received", { xmlns: NS.receipts, id }),
              group ? xml("group", { xmlns: NS.group, jid: chat }) : null,
              xml("store", { xmlns: NS.hints }),
            ),
          );
        }
        // The placeholder couldn't know it mentioned us.
        const c = this.chatMap.get(chat);
        if (c && !c.mention && from !== this.me && content.mentions?.some((x) => x.jid === this.me || x.jid === chat) && !this.isViewing(chat)) this.saveChat({ ...c, mention: true });
      }
      return { done: true };
    }
    if (payload.op === "edit" && typeof payload.id === "string") {
      await this.applyEditValues(chat, from, payload.id, str(payload.body, 65_536), sanitizePreview(payload.preview), ts);
      return { done: true };
    }
    if (payload.op === "live" && typeof payload.id === "string") {
      await this.applyLiveValues(chat, from, payload.id, sanitizeLoc(payload.loc), payload.stop === true, ts);
      return { done: true };
    }
    if (payload.op !== "msg") return { done: true };
    return { done: false, content: sanitizeContent(payload), sent: sentTime(payload.sent, ts) };
  }

  // ---- polls and events ------------------------------------------------------------
  // Votes, RSVPs and cancellations are encrypted ops sent to the chat (every
  // member and our other devices); each updates the poll/event message. One
  // that arrives before its message waits in kv "responses" until it lands.

  /** Vote (replaces this person's earlier vote); [] takes it back. */
  async vote(m: Msg, choices: string[]) {
    if (m.kind !== "poll" || !m.poll || m.deleted) return;
    const ids = new Set(m.poll.options.map((o) => o.id));
    choices = [...new Set(choices)].filter((c) => ids.has(c));
    if (!m.poll.multi) choices = choices.slice(0, 1);
    await this.respond(m, { op: "vote", choices });
  }

  async rsvp(m: Msg, answer: RSVP) {
    if (m.kind !== "event" || !m.event || m.event.canceled || m.deleted) return;
    await this.respond(m, { op: "rsvp", answer });
  }

  /** Organizer: cancel the event for everyone. */
  async cancelEvent(m: Msg) {
    if (m.kind !== "event" || m.dir !== "out" || m.event?.canceled) return;
    await this.respond(m, { op: "event-cancel" });
  }

  /** Pin a message to the top of the chat for `days` (0 unpins). */
  async pinMessage(m: Msg, days: number) {
    if (m.deleted || m.kind === "system" || !this.canPin(m.chat)) return;
    await this.respond(m, { op: "pin", until: days ? Date.now() + days * 86_400_000 : 0 });
  }

  /** Groups whose info only admins may edit: only admins pin. */
  canPin(chat: string) {
    const g = S.groups.value.get(chat);
    return !S.isChannel(chat) && !S.isBroadcast(chat) && (!g?.only_admins_edit || S.isGroupAdmin(chat, this.me));
  }

  /** Keep a disappearing message (it stops disappearing), or let it go again. */
  async keepMessage(m: Msg, keep: boolean) {
    if (!m.ttl || m.deleted) return;
    await this.respond(m, { op: "keep", kept: keep });
  }

  private async respond(m: Msg, r: { op: Response["op"]; choices?: string[]; answer?: RSVP; until?: number; kept?: boolean }) {
    const ts = Date.now();
    await this.enqueue(() => this.applyResponse(m.chat, m.key, { ...r, from: this.me, ts }));
    await this.sendEncrypted(m.chat, uuid(), r.op, { op: r.op, id: m.id, choices: r.choices, answer: r.answer, until: r.until, kept: r.kept });
  }

  // ---- contact sync ----------------------------------------------------------------
  // The phone app syncs its address book (contacts.ts); the names it has for
  // people on xmm go to our other devices end-to-end encrypted.

  private shareBookTimer = 0;
  private syncingContacts: Promise<import("./contacts").SyncResult> | null = null;

  /** Phone app: sync the address book now (asks for the permission). */
  syncContacts(book?: import("./contacts").AddressBook) {
    this.syncingContacts ??= (async () => {
      const { syncContacts } = await import("./contacts");
      try {
        return await syncContacts(
          {
            me: this.me,
            contactBookAuto: async () => ((await this.db.get("kv", "contactbook")) as ContactBook | undefined)?.auto ?? [],
            saveContactBook: (names, auto, fp) => this.saveContactBook({ names, auto, fp, at: Date.now() }),
          },
          book,
        );
      } finally {
        this.syncingContacts = null;
      }
    })();
    return this.syncingContacts;
  }

  /** Phone app, automatically (start-up, back to the app): sync when the
   * address book changed, or every 12 hours (people who joined xmm since).
   * The first time asks for the permission; later only checks it. */
  async maybeSyncContacts(): Promise<import("./contacts").SyncResult | null> {
    if (this.syncingContacts || this.stopped) return null;
    const { readAddressBook, bookFingerprint, NoContactsPermission } = await import("./contacts");
    const cur = (await this.db.get("kv", "contactbook")) as ContactBook | undefined;
    const asked = !!(await this.db.get("kv", "contacts:asked"));
    try {
      if (!asked) await this.db.put("kv", true, "contacts:asked");
      const book = await readAddressBook(!asked);
      if (cur && cur.fp === (await bookFingerprint(book)) && Date.now() - cur.at < 12 * 3_600_000) return null;
      return await this.syncContacts(book);
    } catch (e) {
      if (!(e instanceof NoContactsPermission)) console.warn("contact sync", e);
      return null;
    }
  }

  private async saveContactBook(book: ContactBook) {
    await this.db.put("kv", book, "contactbook");
    S.contactNames.value = new Map(Object.entries(book.names));
    await this.shareContactBook();
  }

  /** Send the names to our other devices (a newly linked one too). */
  async shareContactBook() {
    if (this.stopped) return;
    const book = (await this.db.get("kv", "contactbook")) as ContactBook | undefined;
    if (!book || !E2E_ON) return;
    await this.sendEncrypted(this.me, uuid(), "contacts", { op: "contacts", names: book.names, at: book.at }).catch((e) => console.warn("share contacts", e));
  }

  private async applyContactBook(p: Record<string, unknown>) {
    const at = numv(p.at) ?? 0;
    const cur = (await this.db.get("kv", "contactbook")) as ContactBook | undefined;
    if (cur && cur.at >= at) return; // an older copy (archive order)
    const names: Record<string, string> = {};
    if (p.names && typeof p.names === "object")
      for (const [j, n] of Object.entries(p.names as Record<string, unknown>).slice(0, 20_000))
        if (/^\d{6,16}@[^@\s/]+$/.test(j) && typeof n === "string" && n.trim()) names[j] = n.trim().slice(0, 80);
    await this.db.put("kv", { names, auto: cur?.auto ?? [], at, fp: cur?.fp }, "contactbook");
    S.contactNames.value = new Map(Object.entries(names));
  }

  /** Chat lock: the list, also for the service worker (push notifications). */
  async saveLockedChats(chats: string[]) {
    await this.db.put("kv", chats, "chatlock");
  }

  /** Store a message's translation (this device only); null forgets it. */
  async setTranslation(m: Msg, t: Msg["translation"] | null) {
    await this.enqueue(() => this.updateMessage(m.key, (x) => ({ ...x, translation: t ? { ...t, text: t.text.slice(0, 70_000) } : undefined })));
  }

  /** Store a voice message's transcript (this device only). */
  async setTranscript(m: Msg, text: string) {
    await this.enqueue(() => this.updateMessage(m.key, (x) => ({ ...x, transcript: text.slice(0, 20_000) })));
  }

  /** Every message of a chat, oldest first (export). */
  allMessages(chat: string): Promise<Msg[]> {
    return this.db.getAllFromIndex("messages", "byChatTs", IDBKeyRange.bound([chat, -Infinity], [chat, Infinity]));
  }

  /** The active chat's pinned messages (newest pin first, at most 3). */
  async refreshPins(chat = S.activeChat.value) {
    if (!chat) return;
    const all = await this.db.getAllFromIndex("messages", "byChatTs", IDBKeyRange.bound([chat, -Infinity], [chat, Infinity]));
    const now = Date.now();
    const pins = all.filter((m) => m.pin && m.pin.until > now && !m.deleted && !m.hidden).sort((a, b) => b.pin!.ts - a.pin!.ts).slice(0, 3);
    if (S.activeChat.value === chat) S.pinned.value = pins;
  }

  /** Runs inside the engine's queue (incoming stanzas already do). */
  private async applyResponse(chat: string, key: string, r: Response) {
    if (!chat) return;
    // Pinning in a group whose info only admins may edit: admins only.
    if (r.op === "pin") {
      const g = S.groups.value.get(chat);
      if (S.isChannel(chat) || (g?.only_admins_edit && !S.isGroupAdmin(chat, r.from))) return;
    }
    if (await this.db.get("messages", key)) {
      const next = await this.updateMessage(key, (m) => withResponse(m, r));
      if (r.op === "pin" && next?.pin) await this.systemNotice(chat, "pin-" + next.id + "-" + r.ts, "pinned", r.from, "", r.ts);
      if (r.op === "pin") await this.refreshPins(chat);
      return;
    }
    // The poll/event itself hasn't arrived yet (archive order): keep it.
    const pending = ((await this.db.get("kv", "responses")) as Record<string, Response[]> | undefined) ?? {};
    const list = [...(pending[key] ?? []), r].slice(-500);
    await this.db.put("kv", { ...pending, [key]: list }, "responses");
  }

  // ---- group history for new members ------------------------------------------------
  // A group with "share_history" on gives new members its recent messages
  // (the last 14 days, up to 100): the admin who adds them seals those with a
  // fresh key, uploads the ciphertext and sends the key end-to-end encrypted
  // to each new member. Someone joining by invite link asks an admin.

  /** Admin: send the group's recent messages to members just added. */
  async shareGroupHistory(room: string, members: string[]) {
    const g = S.groups.value.get(room);
    if (!g?.share_history || !members.length || !this.isGroupAdminMe(room)) return;
    const since = Date.now() - GROUP_HISTORY_DAYS * 86_400_000;
    const messages = (await messagesPage(this.db, room, GROUP_HISTORY_MAX * 3))
      .filter((m) => m.ts >= since && !m.deleted && !m.undecryptable && m.kind !== "system" && (!m.expiresAt || m.expiresAt > Date.now()))
      .slice(-GROUP_HISTORY_MAX)
      .map((m): Msg => {
        const x = m.viewOnce ? viewedCopy(m) : m;
        // As the new member sees them: all incoming, none of our ticks.
        return { ...x, from: m.dir === "out" ? this.me : m.from, dir: "in", status: Status.Read, receipts: undefined, media: x.media ? { ...x.media, blob: undefined } : undefined };
      });
    if (!messages.length) return;
    const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
    const bundle: Bundle = { v: 1, account: room, exportedAt: Date.now(), chats: [], messages, profiles: [] };
    const up = await api.upload(await seal(key, bundle), "group-history.bin");
    const payload = { op: "ghist", room, url: up.url, key: b64u.from(await crypto.subtle.exportKey("raw", key)), n: messages.length };
    for (const j of members)
      await this.sendEncrypted(j, uuid(), "ghist", payload).catch((e) => console.warn("group history to", j, e));
  }

  private isGroupAdminMe(room: string) {
    return S.isGroupAdmin(room, this.me);
  }

  /** Joined by invite link: ask one admin (the owner first) for the recent messages. */
  async requestGroupHistory(room: string) {
    const g = S.groups.value.get(room);
    if (!g?.share_history) return;
    const admins = g.members.filter((m) => m.jid !== this.me && (m.role === "owner" || m.role === "admin"));
    const admin = admins.find((m) => m.role === "owner") ?? admins[0];
    if (admin) await this.send(xml("message", { to: admin.jid, type: "normal", id: uuid() }, xml("history-request", { xmlns: NS.group, room }), xml("store", { xmlns: NS.hints })));
  }

  private historyAnswered = new Set<string>();

  private async answerGroupHistoryRequest(requester: string, room: string) {
    const key = room + "\u0000" + requester;
    if (this.historyAnswered.has(key)) return;
    const g = S.groups.value.get(room);
    const member = g?.members.find((m) => m.jid === requester);
    // Only for someone who joined in the last day.
    if (!g?.share_history || !member || Date.now() - member.joined_at * 1000 > 24 * 3600_000) return;
    this.historyAnswered.add(key);
    await this.shareGroupHistory(room, [requester]).catch((e) => console.warn("group history", e));
  }

  /** From an admin of a group we're in: import its recent messages. */
  private async receiveGroupHistory(from: string, p: Record<string, unknown>) {
    const room = str(p.room, 300);
    if (!S.isGroup(room) || typeof p.url !== "string" || typeof p.key !== "string") return;
    if (!S.groups.value.get(room)?.members.some((m) => m.jid === from)) await this.loadGroups();
    const g = S.groups.value.get(room);
    const sender = g?.members.find((m) => m.jid === from);
    if (!g || !sender || !(sender.role === "owner" || sender.role === "admin") || !g.members.some((m) => m.jid === this.me)) return;
    try {
      const res = await fetch(p.url);
      if (!res.ok) throw new Error("download failed");
      const key = await crypto.subtle.importKey("raw", b64u.to(p.key), "AES-GCM", false, ["decrypt"]);
      const b = await unseal(key, await res.arrayBuffer());
      let added = 0;
      await this.enqueue(async () => {
        for (const m of b.messages.slice(0, GROUP_HISTORY_MAX)) {
          if (m.chat !== room || m.key !== msgKey(room, m.id) || m.kind === "system") continue;
          const cur = await this.db.get("messages", m.key);
          if (cur && !cur.undecryptable) continue;
          await this.db.put("messages", { ...m, dir: "in", status: Status.Read, hidden: undefined });
          added++;
        }
      });
      if (!added) return;
      await this.systemNotice(room, "ghist-" + uuid(), "history-shared", from, String(added), Math.min(...b.messages.map((m) => m.ts)) - 1);
      if (S.activeChat.value === room) await this.openChat(room);
    } catch (e) {
      console.warn("group history", e);
    }
  }

  // ---- retry receipts ------------------------------------------------------------
  // A device that gets a 1:1 message not encrypted for it asks the sender to
  // re-encrypt it; the sender refreshes its view of our devices and re-sends
  // the same message (same id) to us only, without archiving it again.

  private retried = new Map<string, number>();

  /** Stored (offline delivery + archive), so a sender that's offline right
   * now re-sends when it comes back. */
  private async requestRetry(sender: string, id: string, group?: string) {
    await this.send(xml("message", { to: sender, type: "normal", id: uuid() }, xml("retry", { xmlns: NS.e2e, id, device: String(this.e2e.deviceId), group }), xml("store", { xmlns: NS.hints })));
  }

  private async onRetryRequest(requester: string, el: Element) {
    const id = el.attrs.id;
    if (!id || requester === this.me || S.isGroup(requester) || S.blocked.value.has(requester)) return;
    const group = el.attrs.group;
    if (group) return this.onGroupRetry(requester, group, id, el.attrs.device);
    const key = msgKey(requester, id);
    const n = this.retried.get(key) ?? 0;
    if (n >= 2) return; // at most twice per message
    const m = await this.db.get("messages", key);
    // (Never one this device couldn't read itself: there's nothing to re-send.)
    if (!m || m.dir !== "out" || m.kind === "system" || m.deleted || m.undecryptable || Date.now() - m.ts > 24 * 3600_000) return;
    this.retried.set(key, n + 1);
    this.e2e.forget(requester);
    try {
      // Only for messages sent after the requesting device's session began,
      // or that never reached any of the recipient's devices (that isn't
      // history: it's a message still on its way).
      const dev = (await this.e2e.devices([requester])).get(requester)?.find((d) => String(d.device_id) === el.attrs.device);
      if (!dev?.since || (m.ts < dev.since && m.status >= Status.Delivered)) return;
      const env = await this.e2e.encrypt([requester], contentPayload(m));
      await this.send(
        xml(
          "message",
          { to: requester, type: "chat", id: m.id },
          xml("body", {}, E2E_FALLBACK),
          envelopeEl(env, "msg"),
          xml("request", { xmlns: NS.receipts }),
          xml("no-store", { xmlns: NS.hints }),
        ),
      );
    } catch (e) {
      console.warn("retry", e);
    }
  }

  /** A group member's device couldn't read our group message: send it again
   * through the group, encrypted for that one device only (everyone else
   * already has it and ignores the copy). */
  private async onGroupRetry(requester: string, room: string, id: string, device?: string) {
    const g = S.groups.value.get(room);
    if (!S.isGroup(room) || !g?.members.some((x) => x.jid === requester)) return;
    const key = msgKey(room, id) + "\u0000" + requester + "/" + device;
    const n = this.retried.get(key) ?? 0;
    if (n >= 2) return;
    const m = await this.db.get("messages", msgKey(room, id));
    // (Never one this device couldn't read itself: there's nothing to re-send.)
    if (!m || m.dir !== "out" || m.kind === "system" || m.deleted || m.undecryptable || Date.now() - m.ts > 24 * 3600_000) return;
    this.retried.set(key, n + 1);
    this.e2e.forget(requester);
    try {
      const dev = (await this.e2e.devices([requester])).get(requester)?.find((d) => String(d.device_id) === device);
      // Never history from before that device existed, unless none of the
      // member's devices got it yet.
      const got = m.receipts?.[requester];
      if (!dev?.since || (m.ts < dev.since && (got?.d || got?.r))) return;
      const env = await this.e2e.encryptForDevice(requester, dev.device_id, contentPayload(m));
      await this.send(
        xml("message", { to: room, type: "groupchat", id: m.id }, xml("body", {}, E2E_FALLBACK), envelopeEl(env, "msg"), xml("origin-id", { xmlns: NS.sid, id: m.id }), xml("no-store", { xmlns: NS.hints })),
      );
    } catch (e) {
      console.warn("group retry", e);
    }
  }

  /** The restore offer on a new main phone: dismissed, or done. */
  async dismissRestoreOffer() {
    await this.db.delete("kv", "history:offer-restore");
    S.offerRestore.value = false;
  }

  /** The main phone starts from a backup (like WhatsApp): messages from
   * before it existed that it can't read (the archive, encrypted for the old
   * devices) aren't shown as "waiting" placeholders. One that never reached
   * any device is re-sent by its sender (openEncrypted asked) and lands then. */
  private skipOldUnreadable(c: Content, ts: number) {
    return !!c.undecryptable && onPhone && !this.session.linked && ts < this.e2e.createdAt;
  }

  private isUser(jid: string) {
    return jid.endsWith("@" + this.session.xmpp.domain);
  }

  // ---- history sync -------------------------------------------------------------------

  private historyBusy = false;
  private historyAgain = false;

  /** History sync. The main phone (logged in by SMS) gets no history from
   * other devices or the server, like WhatsApp: only what the user restores
   * from a backup (Google Drive, iCloud, a file, an xmm backup). Companions
   * get theirs from the phone when it links them. (A browser logged in by
   * SMS, test servers only, still asks the account's devices for it.) */
  async syncHistory(): Promise<void> {
    // A nudge while a sync is running (a new device's request) mustn't be lost.
    if (this.historyBusy) {
      this.historyAgain = true;
      return;
    }
    this.historyBusy = true;
    this.historyAgain = false;
    try {
      const { requests, first, others, scope, primary } = await api.historyRequests();
      if (primary) {
        // Nothing to ask for: an account moved to this phone starts empty and
        // is offered a restore from the user's backups.
        if (!(await this.db.get("kv", "history:done"))) {
          await this.db.put("kv", true, "history:done");
          if (first) await this.db.put("kv", true, "history:full");
          else await this.db.put("kv", true, "history:offer-restore");
        }
        await this.db.put("kv", true, "backup:checked");
        S.offerRestore.value = !!(await this.db.get("kv", "history:offer-restore"));
        S.historyState.value = "none";
        // It still answers other devices' requests (browsers logged in by
        // SMS on test servers) with what it has.
        const hasAll = !!(await this.db.get("kv", "history:full"));
        for (const r of requests) if (!r.mine && r.status === "pending" && r.pub) void this.autoSendHistory(r, hasAll);
        return;
      }
      // A new device first restores the account's automatic backup; with no
      // other device left, that's all the history there is.
      if (!(await this.db.get("kv", "backup:checked"))) {
        const restored = await this.restoreBackup(scope === "full");
        if (restored && !others) await this.db.put("kv", true, "history:done");
      }
      // Only the account's first device starts with the whole history; any
      // later one asks for it.
      if (first) {
        await this.db.put("kv", true, "history:done");
        // (Logged back in after leaving every device: the backup decided.)
        if (!(await this.db.get("kv", "backup:restored"))) await this.db.put("kv", true, "history:full");
      } else if ((await this.db.get("kv", "history:done")) && !(await this.db.get("kv", "history:checked"))) {
        // Earlier versions marked every primary as "has history". Keep that
        // only if this device really has no unreadable messages.
        const unreadable = (await this.db.getAll("messages")).some((m) => m.undecryptable && !m.hidden);
        if (unreadable) await this.db.delete("kv", "history:done");
      }
      await this.db.put("kv", true, "history:checked");
      const hasHistory = !!(await this.db.get("kv", "history:done"));
      const hasAll = !!(await this.db.get("kv", "history:full"));
      if (hasHistory) for (const r of requests) if (!r.mine && r.status === "pending" && r.pub) void this.autoSendHistory(r, hasAll);
      if (hasHistory) {
        S.historyState.value = "none";
        this.markBackupDirty();
        return;
      }
      // Restored from the backup: the rest (messages since) can still come
      // from another device, without a banner.
      const waiting = (await this.db.get("kv", "backup:restored")) ? "none" : "waiting";
      const mine = requests.find((r) => r.mine);
      const keys = (await this.db.get("kv", "history:key")) as { priv: CryptoKey; pub: string } | undefined;
      if (!mine || !keys) {
        const k = await newLinkKeys();
        const pub = await exportPublic(k.publicKey);
        await this.db.put("kv", { priv: k.privateKey, pub }, "history:key");
        await api.requestHistory(pub);
        S.historyState.value = waiting;
        return;
      }
      if (mine.status === "pending") {
        S.historyState.value = waiting;
      } else if (mine.status === "declined") {
        await this.db.put("kv", true, "history:done");
        S.historyState.value = "declined";
      } else if (mine.transfer) {
        S.historyState.value = "importing";
        const key = await linkKey(keys.priv, mine.transfer.pub);
        const res = await fetch(mine.transfer.url);
        if (!res.ok) throw new Error("history download failed");
        const bundle = await unseal(key, await res.arrayBuffer());
        const n = await this.importHistory(bundle);
        if (bundle.complete) await this.db.put("kv", true, "history:full");
        await this.db.put("kv", true, "history:done");
        await this.db.delete("kv", "history:key");
        S.historyState.value = "done";
        const what = bundle.complete ? "your chat history" : `the last ${HISTORY_DAYS} days`;
        window.dispatchEvent(new CustomEvent("xmm-toast", { detail: `Chat history restored: ${what} (${n} messages)` }));
        this.markBackupDirty();
      }
    } catch (e) {
      console.warn("history sync", e);
      if (S.historyState.value === "importing") S.historyState.value = "waiting";
    } finally {
      this.historyBusy = false;
    }
    if (this.historyAgain) return this.syncHistory();
  }

  // ---- automatic history backup ---------------------------------------------------
  // Every device that has the history merges it into the account's backup on
  // the server (sealed with the account's backup key), so a new device gets
  // its chats even when no other device is online or left.

  private backupTimer = 0;
  private backupRun: Promise<void> | null = null;

  /** Something changed: back up within a minute. */
  private markBackupDirty() {
    if (!this.backupTimer && !this.stopped)
      this.backupTimer = window.setTimeout(() => {
        this.backupTimer = 0;
        void this.backupNow();
      }, BACKUP_DELAY);
  }

  /** Merge this device's history into the account's backup and upload it. */
  backupNow(): Promise<void> {
    this.backupRun ??= this.uploadBackup().finally(() => (this.backupRun = null));
    return this.backupRun;
  }

  private async uploadBackup(attempt = 0): Promise<void> {
    // Only for browsers logged in by SMS (test servers): the main phone's and
    // its companions' history comes from backups the user makes, and the phone.
    if (onPhone || this.session.linked) return;
    // A new device still waiting for its history has nothing to add.
    if (!(await this.db.get("kv", "history:done")) && !(await this.db.get("kv", "backup:restored"))) return;
    try {
      const { key, version, backup } = await api.historyBackup();
      const k = await backupKey(key);
      let bundle: Bundle = await this.exportAllHistory();
      bundle.cleared = ((await this.db.get("kv", "backup:cleared")) as Record<string, number> | undefined) ?? {};
      if (await this.db.get("kv", "history:full")) bundle.complete = true;
      if (backup) {
        const res = await fetch(backup.url);
        if (!res.ok) throw new Error("backup download failed");
        bundle = mergeBundles(bundle, await unseal(k, await res.arrayBuffer()));
      }
      const up = await api.upload(await seal(k, bundle), "history-backup.bin");
      await api.putHistoryBackup({ media_id: up.id, messages: bundle.messages.length, base: version });
    } catch (e) {
      // 409: another device uploaded meanwhile; merge with that one.
      if (e instanceof ApiError && e.status === 409 && attempt < 3) return this.uploadBackup(attempt + 1);
      console.warn("history backup", e);
      this.markBackupDirty();
    }
  }

  /** New device: import the account's backup (the web keeps the last 30
   * days of it, the phone app everything). Reports whether there was one. */
  private async restoreBackup(full: boolean): Promise<boolean> {
    const { key, backup } = await api.historyBackup();
    if (!backup) {
      await this.db.put("kv", true, "backup:checked");
      return false;
    }
    S.historyState.value = "importing";
    const res = await fetch(backup.url);
    if (!res.ok) throw new Error("backup download failed");
    const all = await unseal(await backupKey(key), await res.arrayBuffer());
    const b = full ? all : lastDays(all, HISTORY_DAYS);
    const n = await this.importHistory(b);
    if (all.cleared) await this.db.put("kv", all.cleared, "backup:cleared");
    if (full && all.complete) await this.db.put("kv", true, "history:full");
    await this.db.put("kv", true, "backup:restored");
    await this.db.put("kv", true, "backup:checked");
    S.historyState.value = "done";
    window.dispatchEvent(new CustomEvent("xmm-toast", { detail: `Chat history restored from your backup (${n} messages)` }));
    return true;
  }

  private autoSending = new Set<string>();

  /** Send a new device of this account its history, without asking. A
   * device with the whole history answers at once; one with only the last 30
   * days waits a few seconds (much longer when the whole history was asked
   * for), so usually the best copy is the one uploaded. */
  private async autoSendHistory(req: import("./api").HistoryRequest, hasAll: boolean) {
    if (this.autoSending.has(req.id)) return;
    this.autoSending.add(req.id);
    try {
      const wait = hasAll ? Math.random() * 1500 : req.scope === "full" ? 20_000 + Math.random() * 10_000 : 5000 + Math.random() * 4000;
      await new Promise((r) => setTimeout(r, wait));
      const { requests } = await api.historyRequests();
      if (!requests.some((r) => r.id === req.id && r.status === "pending")) return;
      const all = req.scope === "full" && hasAll;
      await this.approveHistory(req, all);
      const name = req.device_name || req.platform || "your new device";
      window.dispatchEvent(new CustomEvent("xmm-toast", { detail: `Sent ${all ? "your chat history" : `the last ${HISTORY_DAYS} days of chat history`} to ${name}` }));
    } catch (e) {
      // 404: another device answered first.
      if (!(e instanceof ApiError && e.status === 404)) {
        console.warn("history auto-send", e);
        this.autoSending.delete(req.id);
      }
    }
  }

  /** Seal our history (everything for a phone, else the last 30 days) to the
   * requesting device's key and send it. */
  private async approveHistory(req: import("./api").HistoryRequest, all: boolean) {
    if (!req.pub) return;
    const mine = await newLinkKeys();
    const key = await linkKey(mine.privateKey, req.pub);
    const bundle = all ? { ...(await this.exportAllHistory()), complete: true } : await this.exportHistory();
    const up = await api.upload(await seal(key, bundle), "history.bin");
    await api.approveHistory(req.id, { url: up.url, pub: await exportPublic(mine.publicKey), size: up.size });
  }

  // ---- channels ------------------------------------------------------------------

  isChannelAdmin(jid: string) {
    const r = S.channels.value.get(jid)?.my_role;
    return r === "owner" || r === "admin";
  }

  async loadChannels() {
    try {
      const { channels } = await api.channels();
      S.channels.value = new Map(channels.map((c) => [c.jid, c]));
      await this.db.put("kv", channels, "channels");
      for (const c of channels)
        if (!this.chatMap.has(c.jid)) {
          this.saveChat({ jid: c.jid, lastBody: "", lastTs: c.created_at * 1000, lastDir: "", lastStatus: Status.Pending, unread: 0 });
          void this.backfillChannel(c.jid);
        }
    } catch (e) {
      console.warn("channels", e);
    }
  }

  async loadChannel(jid: string) {
    try {
      const c = await api.channel(jid);
      if (c.my_role) S.channels.value = new Map(S.channels.value).set(jid, c);
      return c;
    } catch {
      return null;
    }
  }

  async createChannel(name: string, description: string, avatarMediaId?: string) {
    const c = await api.createChannel({ name, description, avatar_media_id: avatarMediaId });
    S.channels.value = new Map(S.channels.value).set(c.jid, c);
    this.saveChat({ jid: c.jid, lastBody: "", lastTs: Date.now(), lastDir: "", lastStatus: Status.Pending, unread: 0 });
    return c;
  }

  async follow(jid: string, on: boolean) {
    const c = await api.follow(jid, on);
    const map = new Map(S.channels.value);
    if (on) {
      map.set(jid, c);
      S.channels.value = map;
      if (!this.chatMap.has(jid)) this.saveChat({ jid, lastBody: "", lastTs: Date.now(), lastDir: "", lastStatus: Status.Pending, unread: 0 });
      await this.backfillChannel(jid);
    } else {
      map.delete(jid);
      S.channels.value = map;
      await this.deleteChat(jid);
    }
    return c;
  }

  async deleteChannel(jid: string) {
    await api.deleteChannel(jid);
    this.channelGone(jid);
  }

  private channelGone(jid: string) {
    const map = new Map(S.channels.value);
    map.delete(jid);
    S.channels.value = map;
    void this.deleteChat(jid);
  }

  /** Recent posts from the API (history before we followed, or a preview). */
  async backfillChannel(jid: string, before?: number): Promise<number> {
    let posts: ChannelPost[];
    try {
      posts = (await api.channelPosts(jid, before)).posts;
    } catch {
      return 0;
    }
    const admin = this.isChannelAdmin(jid);
    let added = 0;
    await this.enqueue(async () => {
      for (const p of posts.slice().reverse()) {
        if (p.deleted || !p.content) continue;
        const c = p.content;
        const kind = (KINDS.includes(c.kind as Kind) ? c.kind : "text") as Kind;
        const msg: Msg = {
          key: msgKey(jid, p.id),
          id: p.id,
          chat: jid,
          from: jid,
          dir: admin ? "out" : "in",
          kind: c.media && p.media_url ? kind : "text",
          body: c.body ?? "",
          ts: p.created_at,
          status: Status.Sent,
          media:
            c.media && p.media_url
              ? { url: p.media_url, mime: c.media.mime, size: c.media.size, name: c.media.name, width: c.media.width, height: c.media.height, duration: c.media.duration, thumb: safeThumb(c.media.thumb), waveform: c.media.waveform }
              : undefined,
          preview: sanitizePreview(c.preview),
          editedAt: p.edited_at,
          chanReactions: p.reactions,
          myReaction: p.my_reaction,
        };
        if (await this.insertMessage(msg)) {
          added++;
          this.touchChat(msg, false);
        } else if (p.reactions) {
          await this.updateMessage(msg.key, (x) => ({ ...x, chanReactions: p.reactions, myReaction: p.my_reaction }));
        }
      }
    });
    if (S.activeChat.value === jid) S.messages.value = await messagesPage(this.db, jid, PAGE);
    return added;
  }

  /** Room message from the channel (only its "system" account may post). */
  private async handleChannel(room: string, nick: string, m: Element, id: string | undefined, ts: number, stanzaId: string | undefined, ctx: Ctx) {
    if (nick !== "system") return; // the server drops these too
    if (m.getChild("info", NS.channel)) {
      if (!ctx.archived) void this.loadChannel(room);
      return;
    }
    if (m.getChild("deleted", NS.channel)) {
      if (!ctx.archived) this.channelGone(room);
      return;
    }
    const counts = m.getChild("counts", NS.channel);
    if (counts?.attrs.id) {
      const r: Record<string, number> = {};
      for (const e of counts.getChildren("r").slice(0, 50)) if (e.attrs.e && e.attrs.e.length <= 32) r[e.attrs.e] = Number(e.attrs.n) || 0;
      await this.updateMessage(msgKey(room, counts.attrs.id), (x) => ({ ...x, chanReactions: r }));
      return;
    }
    const replace = m.getChild("replace", NS.correct);
    if (replace?.attrs.id) return this.applyEdit(room, room, replace.attrs.id, m, ts);
    const retract = m.getChild("retract", NS.retract);
    if (retract?.attrs.id) return this.applyRetract(room, room, retract.attrs.id);
    if (!m.getChild("post", NS.channel) || !id) return;
    const admin = this.isChannelAdmin(room);
    const msg: Msg = {
      key: msgKey(room, id),
      id,
      chat: room,
      from: room,
      dir: admin ? "out" : "in",
      ...parseContent(m, m.getChildText("body") ?? ""),
      ts,
      status: Status.Sent,
      stanzaId,
    };
    if (!(await this.insertMessage(msg))) {
      if (admin) await this.updateMessage(msg.key, (x) => (x.status < Status.Sent ? { ...x, status: Status.Sent } : null));
      return;
    }
    const viewing = this.isViewing(room);
    this.touchChat(msg, !admin && !viewing);
    if (viewing && !ctx.archived) await this.markRead(room);
  }

  /** An admin's post goes through the API; the room echo confirms it. */
  private async transmitPost(m: Msg) {
    try {
      const md = m.media;
      const mediaId = md?.url?.split("/").pop();
      await api.createPost(m.chat, {
        id: m.id,
        kind: md ? m.kind : "text",
        body: m.body,
        media: md && mediaId
          ? { id: mediaId, mime: md.mime, size: md.size, name: md.name, width: md.width, height: md.height, duration: md.duration, thumb: md.thumb, waveform: md.waveform }
          : undefined,
        preview: m.preview,
      });
      await this.enqueue(() => this.applyStatusOne(m.chat, m.id, Status.Sent));
    } catch (e) {
      if (e instanceof ApiError && e.code === "exists") return this.enqueue(() => this.applyStatusOne(m.chat, m.id, Status.Sent));
      console.warn("channel post", e);
      await this.markFailed(m);
    }
  }

  private async reactPost(m: Msg, emoji: string | null) {
    const next = emoji && emoji !== m.myReaction ? emoji : null;
    try {
      const r = await api.reactPost(m.chat, m.id, next);
      await this.enqueue(() => this.updateMessage(m.key, (x) => ({ ...x, chanReactions: r.reactions, myReaction: r.my_reaction || undefined })));
    } catch (e) {
      console.warn("react", e);
      window.dispatchEvent(new CustomEvent("xmm-toast", { detail: e instanceof Error ? e.message : String(e) }));
    }
  }

  // ---- broadcast lists ---------------------------------------------------------------

  async loadBroadcasts() {
    try {
      const { broadcasts } = await api.broadcasts();
      S.broadcasts.value = new Map(broadcasts.map((b) => [S.broadcastJid(b.id), b]));
      for (const b of broadcasts) {
        const jid = S.broadcastJid(b.id);
        if (!this.chatMap.has(jid)) this.saveChat({ jid, lastBody: "", lastTs: b.created_at * 1000, lastDir: "", lastStatus: Status.Pending, unread: 0 });
      }
    } catch (e) {
      console.warn("broadcasts", e);
    }
  }

  async saveBroadcast(name: string, members: string[], id = uuid()) {
    const { broadcasts } = await api.putBroadcast(id, name, members);
    S.broadcasts.value = new Map(broadcasts.map((b) => [S.broadcastJid(b.id), b]));
    const jid = S.broadcastJid(id);
    if (!this.chatMap.has(jid)) this.saveChat({ jid, lastBody: "", lastTs: Date.now(), lastDir: "", lastStatus: Status.Pending, unread: 0 });
    return jid;
  }

  /** One copy per recipient (same id), each a normal encrypted 1:1 message. */
  private async transmitBroadcast(m: Msg) {
    const list = S.broadcasts.value.get(m.chat);
    if (!list) return this.markFailed(m);
    const to = list.members.filter((x) => x.receives).map((x) => x.jid);
    if (!to.length) return this.markFailed(m);
    for (const jid of to) {
      const copy: Msg = { ...m, key: msgKey(jid, m.id), chat: jid, bcast: m.chat, status: Status.Pending };
      await this.enqueue(async () => {
        if (await this.insertMessage(copy)) this.touchChat(copy, false);
      });
      const cur = await this.db.get("messages", copy.key);
      if (cur && cur.status === Status.Pending) await this.transmit(cur);
    }
  }

  /** A list message shows the lowest status among its copies. */
  private async aggregateBroadcast(listJid: string, id: string) {
    const list = S.broadcasts.value.get(listJid);
    const own = await this.db.get("messages", msgKey(listJid, id));
    if (!list || !own) return;
    let min: Status = Status.Read;
    let any = false;
    for (const mem of list.members.filter((x) => x.receives)) {
      const c = await this.db.get("messages", msgKey(mem.jid, id));
      if (!c) continue;
      any = true;
      if (c.status < min) min = c.status;
    }
    if (any && min > own.status) await this.applyStatusOne(listJid, id, min);
  }

  // ---- communities ------------------------------------------------------------------

  async loadCommunities() {
    try {
      S.communities.value = (await api.communities()).communities;
    } catch (e) {
      console.warn("communities", e);
    }
  }

  // ---- status --------------------------------------------------------------------------

  private statusTimer = 0;
  refreshStatusesSoon(ms = 400) {
    clearTimeout(this.statusTimer);
    this.statusTimer = window.setTimeout(() => void this.enqueue(() => this.loadStatuses()), ms);
  }

  /** Decrypt a status once (a Signal message key works only once) and keep
   * the plaintext on this device until it expires. */
  private async openStatus(it: StatusItem, viewed: Set<string>): Promise<S.StatusView> {
    const cacheKey = "st:" + it.id;
    let content = (await this.db.get("kv", cacheKey)) as Record<string, unknown> | undefined;
    if (!content) {
      try {
        content = (await this.e2e.decrypt(it.owner, it.envelope as Envelope)) as Record<string, unknown>;
        if (content?.op !== "status") throw new Error("not a status");
        await this.db.put("kv", { ...content, expiresAt: it.expires_at }, cacheKey);
      } catch (e) {
        console.warn("status decrypt", it.id, (e as Error).message);
        return { id: it.id, owner: it.owner, kind: it.kind, createdAt: it.created_at, expiresAt: it.expires_at, viewed: true, failed: true };
      }
    }
    return statusView(it, content, viewed.has(it.id) || !!it.viewed);
  }

  async loadStatuses() {
    try {
      const [feed, mine, aud] = await Promise.all([api.statusFeed(), api.myStatuses(), api.statusAudience()]);
      S.statusPrivacy.value = aud.privacy;
      const viewed = new Set(((await this.db.get("kv", "status:viewed")) as string[] | undefined) ?? []);
      const byOwner = new Map<string, S.StatusView[]>();
      for (const it of feed.statuses) {
        if (S.blocked.value.has(it.owner)) continue;
        const v = await this.openStatus(it, viewed);
        if (v.failed) continue;
        byOwner.set(it.owner, [...(byOwner.get(it.owner) ?? []), v]);
      }
      S.statuses.value = byOwner;
      const own: S.StatusView[] = [];
      for (const it of mine.statuses) {
        const v = await this.openStatus({ ...it, owner: this.me }, viewed);
        own.push({ ...v, views: it.views, viewed: true });
      }
      S.myStatuses.value = own;
      void this.ensureProfiles([...byOwner.keys()]);
      // Forget plaintext of expired statuses.
      const live = new Set([...feed.statuses, ...mine.statuses].map((x) => "st:" + x.id));
      for (const k of await this.db.getAllKeys("kv"))
        if (typeof k === "string" && k.startsWith("st:") && !live.has(k)) await this.db.delete("kv", k);
    } catch (e) {
      console.warn("statuses", e);
    }
  }

  async postStatus(input: ({ kind: "text"; text: string; bg: string; font: number } | { kind: "image" | "video"; file: Blob; caption: string; width?: number; height?: number; duration?: number; thumb?: string }) & { mentions?: string[] }) {
    const id = uuid();
    const content: Record<string, unknown> = { op: "status", v: 1, kind: input.kind };
    let mediaId: string | undefined;
    if (input.kind === "text") {
      Object.assign(content, { text: input.text.slice(0, 700), bg: input.bg, font: input.font });
    } else {
      const { E2E } = await e2eModule();
      const sealed = await E2E.encryptBlob(input.file);
      const up = await api.upload(sealed.blob, "encrypted.bin");
      mediaId = up.id;
      Object.assign(content, {
        caption: input.caption.slice(0, 700),
        media: { url: up.url, key: sealed.key, iv: sealed.iv, sha256: sealed.sha256, mime: input.file.type, width: input.width, height: input.height, duration: input.duration, thumb: input.thumb },
      });
    }
    const { audience } = await api.statusAudience();
    // Only people who can see it can be mentioned in it.
    const mentions = [...new Set(input.mentions ?? [])].filter((j) => audience.includes(j)).slice(0, 20);
    if (mentions.length) content.mentions = mentions;
    const env = await this.e2e.encryptAvailable(audience, content);
    await api.postStatus({ id, kind: input.kind, envelope: env, media_id: mediaId, audience: env.keys.map((k) => k.jid).filter((j, i, a) => j !== this.me && a.indexOf(j) === i) });
    // This device has no key entry for itself: keep the plaintext now.
    await this.db.put("kv", { ...content, expiresAt: Date.now() + 86_400_000 }, "st:" + id);
    await this.loadStatuses();
    // Tell the people mentioned, in your chat with them (like WhatsApp).
    const text = (input.kind === "text" ? input.text : input.caption).slice(0, 200) || undefined;
    for (const j of mentions)
      await this.sendDraft(j, { body: "Mentioned you in their status", statusRef: { id, kind: input.kind, text }, quiet: true }).catch((e) => console.warn("status mention", e));
  }

  /** React to someone's status ("" takes it back). */
  async reactStatus(v: S.StatusView, emoji: string) {
    await api.reactStatus(v.id, emoji);
    const map = new Map(S.statuses.value);
    const list = map.get(v.owner);
    if (list) map.set(v.owner, list.map((x) => (x.id === v.id ? { ...x, viewed: true, reaction: emoji || undefined } : x)));
    S.statuses.value = map;
  }

  /** Post someone's status (one you were mentioned in) as your own. */
  async reshareStatus(v: S.StatusView) {
    if (v.kind === "text") return this.postStatus({ kind: "text", text: v.text ?? "", bg: v.bg ?? STATUS_BGS[0], font: v.font ?? 0 });
    const blob = await (await fetch(await this.statusMediaURL(v))).blob();
    const md = v.media!;
    return this.postStatus({ kind: v.kind, file: blob, caption: v.caption ?? "", width: md.width, height: md.height, duration: md.duration, thumb: md.thumb });
  }

  async viewStatus(v: S.StatusView) {
    if (v.viewed || v.owner === this.me) return;
    const viewed = ((await this.db.get("kv", "status:viewed")) as string[] | undefined) ?? [];
    await this.db.put("kv", [...viewed.slice(-500), v.id], "status:viewed");
    const map = new Map(S.statuses.value);
    const list = map.get(v.owner);
    if (list) map.set(v.owner, list.map((x) => (x.id === v.id ? { ...x, viewed: true } : x)));
    S.statuses.value = map;
    await api.viewStatus(v.id).catch(() => {});
  }

  async deleteStatus(id: string) {
    await api.deleteStatus(id);
    await this.db.delete("kv", "st:" + id);
    S.myStatuses.value = S.myStatuses.value.filter((x) => x.id !== id);
  }

  async setStatusPrivacy(audience: "contacts" | "except" | "only", list: string[]) {
    const r = await api.putStatusPrivacy(audience, list);
    S.statusPrivacy.value = r.privacy;
  }

  replyToStatus(v: S.StatusView, text: string) {
    const summary = v.kind === "text" ? (v.text ?? "") : v.caption || (v.kind === "video" ? "Video" : "Photo");
    return this.sendDraft(v.owner, { body: text, replyTo: { id: "status:" + v.id, from: v.owner, kind: v.kind === "text" ? "text" : v.kind, text: summary.slice(0, 200) } });
  }

  private statusMedia = new Map<string, Promise<string>>();
  /** Object URL of a status photo/video (downloaded and decrypted once). */
  statusMediaURL(v: S.StatusView): Promise<string> {
    const md = v.media;
    if (!md) return Promise.reject(new Error("no media"));
    let p = this.statusMedia.get(v.id);
    if (!p) {
      p = (async () => {
        const res = await fetch(md.url);
        if (!res.ok) throw new Error("status media unavailable");
        const { E2E } = await e2eModule();
        const blob = md.key && md.iv ? await E2E.decryptBlob(await res.arrayBuffer(), md.key, md.iv, md.mime, md.sha256) : await res.blob();
        return URL.createObjectURL(blob);
      })();
      p.catch(() => this.statusMedia.delete(v.id));
      this.statusMedia.set(v.id, p);
    }
    return p;
  }

  // ---- calls ---------------------------------------------------------------------

  /** Record a finished 1:1 call in the chat (we were the caller) and tell the
   * other side's devices and ours, via MAM. */
  private async logCall(chat: string, sid: string, media: CallMedia, result: CallResult, duration: number) {
    const id = uuid();
    const msg: Msg = { key: msgKey(chat, id), id, chat, from: this.me, dir: "out", kind: "call", body: "", ts: Date.now(), status: Status.Sent, call: { sid, media, result, duration } };
    await this.enqueue(async () => {
      if (await this.insertMessage(msg)) this.touchChat(msg, false);
    });
    const what = media === "video" ? "video call" : "voice call";
    await this.send(
      xml(
        "message",
        { to: chat, type: "chat", id },
        xml("call-log", { xmlns: NS_CALL, id: sid, media, result, duration: String(duration) }),
        xml("body", {}, result === "answered" ? `📞 ${what[0].toUpperCase() + what.slice(1)} (${fmtDuration(duration)})` : `📞 Missed ${what}`),
        xml("origin-id", { xmlns: NS.sid, id }),
        xml("store", { xmlns: NS.hints }),
      ),
    );
  }

  private async applyGroupCallEnd(room: string, el: Element) {
    const sid = el.attrs.id;
    if (!sid) return;
    const duration = Math.max(0, Math.min(86400, Number(el.attrs.duration) || 0));
    await this.updateMessage(msgKey(room, sid), (x) => (x.kind === "call" && x.call && !x.call.result ? { ...x, call: { ...x.call, result: "ended", duration } } : null));
  }

  /** All call entries, newest first (Calls screen). */
  async callHistory(limit = 200): Promise<Msg[]> {
    const out: Msg[] = [];
    let cur = await this.db.transaction("messages").store.index("byChatTs").openCursor(null, "prev");
    while (cur && out.length < 5000) {
      if (cur.value.kind === "call" && !cur.value.hidden) out.push(cur.value);
      cur = await cur.continue();
    }
    return out.sort((a, b) => b.ts - a.ts).slice(0, limit);
  }

  /** Everyone whose devices must be able to read a message in `chat`. */
  private recipients(chat: string): string[] {
    if (!S.isGroup(chat)) return [chat];
    return (S.groups.value.get(chat)?.members ?? []).map((m) => m.jid).filter((j) => j !== this.me);
  }

  private retryTimers = new Map<string, number>();
  private retryCounts = new Map<string, number>();

  /** Encrypt `payload` for the chat and send it. */
  private async sendEncrypted(chat: string, id: string, op: "msg" | "edit" | "live" | "ghist" | "vote" | "rsvp" | "event-cancel" | "pin" | "keep" | "contacts", payload: object, extra: (Element | null)[] = [], store = true) {
    // Groups: encrypt for every member who has a device now; members without
    // one (never opened xmm, or still publishing keys right after logging in)
    // don't block the group — see catchUpGroup. 1:1 chats wait for the other
    // person's device instead (NoDevicesError -> queued).
    const group = S.isGroup(chat);
    const recipients = this.recipients(chat);
    const env = group ? await this.e2e.encryptAvailable(recipients, payload) : await this.e2e.encrypt(recipients, payload);
    const stanza = (e: Envelope) =>
      xml(
        "message",
        { to: chat, type: msgType(chat), id },
        xml("body", {}, op === "live" && !store ? "" : E2E_FALLBACK),
        envelopeEl(e, op),
        ...extra,
        xml(store ? "store" : "no-store", { xmlns: NS.hints }),
      );
    await this.send(stanza(env));
    if (group && op === "msg") {
      const covered = new Set(env.keys.map((k) => k.jid));
      const missing = recipients.filter((j) => !covered.has(j));
      if (missing.length) this.catchUpGroup(missing, payload, stanza);
    }
  }

  /** Re-send a group message (same id) to members who had no device when it
   * was sent, once they do: 5 s, 20 s and 60 s later. Everyone else already
   * has it and ignores the copy. */
  private catchUpGroup(missing: string[], payload: object, stanza: (e: Envelope) => Element, delays = [5_000, 20_000, 60_000]) {
    const [delay, ...rest] = delays;
    if (delay === undefined || !missing.length) return;
    window.setTimeout(async () => {
      if (this.stopped) return;
      try {
        for (const j of missing) this.e2e.forget(j);
        const env = await this.e2e.encryptAvailable(missing, payload);
        const got = new Set(env.keys.map((k) => k.jid));
        const reached = missing.filter((j) => got.has(j));
        if (reached.length) await this.send(stanza(env));
        this.catchUpGroup(missing.filter((j) => !got.has(j)), payload, stanza, rest);
      } catch (e) {
        console.warn("group catch-up", e);
      }
    }, delay);
  }


  private async securityNotice(jid: string) {
    if (!this.chatMap.has(jid)) return;
    const id = "sec-" + uuid();
    const msg: Msg = {
      key: msgKey(jid, id),
      id,
      chat: jid,
      from: "system",
      dir: "in",
      kind: "system",
      body: "Security code changed",
      ts: Date.now(),
      status: Status.Pending,
      system: { type: "security", actor: jid, targets: [], value: "" },
    };
    if (await this.insertMessage(msg)) this.touchChat(msg, false);
  }

  async securityCode(jid: string) {
    return this.e2e.securityCode(jid);
  }

  /** Live-location updates / stop from the message's author. */
  private async applyLiveOps(chat: string, from: string, m: Element, ts: number): Promise<boolean> {
    const upd = m.getChild("live-update", NS.live) ?? m.getChild("live-stop", NS.live);
    if (!upd?.attrs.id) return false;
    await this.applyLiveValues(chat, from, upd.attrs.id, parseGeoloc(upd.getChild("geoloc", NS.geoloc)), upd.name === "live-stop", ts);
    return true;
  }

  private async applyLiveValues(chat: string, from: string, id: string, pos: ReturnType<typeof parseGeoloc>, stop: boolean, ts: number) {
    await this.updateMessage(msgKey(chat, id), (x) => {
      if (x.from !== from || !x.location?.live || x.location.live.stopped) return null;
      if (!stop && ts < x.location.live.updatedAt) return null; // stale
      return {
        ...x,
        location: { ...x.location, ...(pos ?? {}), live: { ...x.location.live, updatedAt: ts, stopped: stop || undefined } },
      };
    });
  }

  // ---- sharing our live location -------------------------------------------------

  private liveWatches = new Map<string, { watch: number; timer: number; last: number; pending?: GeolocationPosition }>();

  /** Start sending position updates for our live-location message. */
  private startLive(m: Msg) {
    const until = m.location?.live?.until ?? 0;
    if (this.liveWatches.has(m.key) || until <= Date.now() || typeof navigator === "undefined" || !navigator.geolocation) return;
    const entry = { watch: 0, timer: 0, last: 0 } as { watch: number; timer: number; last: number; pending?: GeolocationPosition };
    const flush = () => {
      const p = entry.pending;
      if (!p) return;
      entry.pending = undefined;
      entry.last = Date.now();
      void this.sendLiveUpdate(m, p);
    };
    entry.watch = navigator.geolocation.watchPosition(
      (p) => {
        entry.pending = p;
        if (Date.now() - entry.last >= LIVE_MIN_INTERVAL) flush();
      },
      (e) => console.warn("live location", e.message),
      { enableHighAccuracy: true, maximumAge: 5000 },
    );
    const tick = window.setInterval(() => {
      if (Date.now() >= until) void this.stopLive(m.key);
      else if (Date.now() - entry.last >= LIVE_MIN_INTERVAL) flush();
    }, 2000);
    entry.timer = tick;
    this.liveWatches.set(m.key, entry);
  }

  private async sendLiveUpdate(m: Msg, p: GeolocationPosition) {
    const loc = { lat: p.coords.latitude, lon: p.coords.longitude, accuracy: Math.round(p.coords.accuracy) };
    const ts = Date.now();
    await this.enqueue(() =>
      this.updateMessage(m.key, (x) =>
        x.location?.live && !x.location.live.stopped ? { ...x, location: { ...x.location, ...loc, live: { ...x.location.live, updatedAt: ts } } } : null,
      ),
    );
    // Position updates are ephemeral (not archived); the start and stop
    // messages carry positions that are.
    if (E2E_ON) {
      await this.sendEncrypted(m.chat, uuid(), "live", { op: "live", id: m.id, loc }, [], false).catch((e) => console.warn("live update", e));
      return;
    }
    await this.send(
      xml("message", { to: m.chat, type: msgType(m.chat), id: uuid() }, xml("live-update", { xmlns: NS.live, id: m.id }, geolocEl(loc)), xml("no-store", { xmlns: NS.hints })),
    );
  }

  async stopLive(key: string) {
    const w = this.liveWatches.get(key);
    if (w) {
      navigator.geolocation.clearWatch(w.watch);
      clearInterval(w.timer);
      this.liveWatches.delete(key);
    }
    const m = await this.db.get("messages", key);
    if (!m?.location?.live || m.location.live.stopped) return;
    const loc = m.location;
    await this.enqueue(() => this.updateMessage(key, (x) => ({ ...x, location: { ...x.location!, live: { ...x.location!.live!, stopped: true, updatedAt: Date.now() } } })));
    if (E2E_ON) {
      await this.sendEncrypted(m.chat, uuid(), "live", { op: "live", id: m.id, loc: { lat: loc.lat, lon: loc.lon, accuracy: loc.accuracy }, stop: true }).catch((e) =>
        console.warn("live stop", e),
      );
      return;
    }
    await this.send(
      xml(
        "message",
        { to: m.chat, type: msgType(m.chat), id: uuid() },
        xml("live-stop", { xmlns: NS.live, id: m.id }, geolocEl(loc)),
        xml("body", {}, "Live location ended"),
        xml("fallback", { xmlns: NS.fallback, for: NS.live }),
        xml("store", { xmlns: NS.hints }),
      ),
    );
  }

  isSharingLive(key: string) {
    return this.liveWatches.has(key);
  }

  /** After a reload, keep sharing live locations that haven't expired. */
  private async resumeLive() {
    const keys = ((await this.db.get("kv", "live")) as string[] | undefined) ?? [];
    const still: string[] = [];
    for (const k of keys) {
      const m = await this.db.get("messages", k);
      if (m?.dir === "out" && m.location?.live && !m.location.live.stopped && m.location.live.until > Date.now()) {
        still.push(k);
        this.startLive(m);
      }
    }
    await this.db.put("kv", still, "live");
  }

  /** Only the author may edit or delete a message. */
  private async applyRetract(chat: string, from: string, id: string) {
    await this.updateMessage(msgKey(chat, id), (m) =>
      m.from !== from || m.deleted
        ? null
        : { ...m, deleted: true, body: "", media: undefined, preview: undefined, reactions: undefined, replyTo: undefined },
    );
  }

  private async applyEdit(chat: string, from: string, id: string, el: Element, ts: number) {
    const body = el.getChildText("body");
    if (body === null) return;
    await this.applyEditValues(chat, from, id, body, parsePreview(el), ts);
  }

  private async applyEditValues(chat: string, from: string, id: string, body: string, preview: LinkPreview | undefined, ts: number) {
    await this.updateMessage(msgKey(chat, id), (m) => {
      if (m.from !== from || m.deleted) return null;
      if (m.editedAt && m.editedAt >= ts) return null; // an older edit arrived late
      return { ...m, body, preview, editedAt: ts };
    });
  }

  private async applyReaction(chat: string, from: string, id: string, emoji?: string) {
    const key = msgKey(chat, id);
    if (!(await this.db.get("messages", key))) {
      // The message hasn't landed yet (still being decrypted, or archive
      // order): keep the reaction until it does (insertMessage applies it).
      const early = ((await this.db.get("kv", "reactions:early")) as Record<string, Record<string, string | null>> | undefined) ?? {};
      const keys = Object.keys(early);
      if (keys.length > 500) delete early[keys[0]];
      early[key] = { ...early[key], [from]: emoji ?? null };
      await this.db.put("kv", early, "reactions:early");
      return;
    }
    await this.updateMessage(key, (m) => {
      if (m.deleted) return null;
      const reactions = { ...m.reactions };
      if (emoji) reactions[from] = emoji;
      else delete reactions[from];
      return { ...m, reactions };
    });
  }

  /** Copy a short preview of the quoted message into the reply. */
  private async snapshotQuote(chat: string, q: Quote): Promise<Quote> {
    const target = await this.db.get("messages", msgKey(q.chat ?? chat, q.id));
    if (!target) return q;
    return { id: q.id, from: target.from, kind: target.kind, text: target.deleted ? "" : summarize(target), chat: q.chat };
  }

  private notify(m: Msg, mentioned = false) {
    window.dispatchEvent(new Event("xmm-incoming")); // native app: haptics
    if (S.isMuted(m.chat) && !mentioned) return; // mentions notify even in muted chats
    // A locked chat: no name, no content.
    const locked = S.lockedChats.value.has(m.chat);
    const group = S.isGroup(m.chat);
    const sender = group ? S.memberName(m.from) : S.displayName(m.chat);
    const title = locked ? "xmm" : group ? `${sender} @ ${S.displayName(m.chat)}` : sender;
    const body = locked ? "New message" : summarize(m);
    const avatar = locked ? null : ((group ? S.groups.value.get(m.chat)?.avatar_url : S.profiles.value.get(m.chat)?.avatar_url) ?? null);
    if (onPhone) {
      // The app's WebView has no Notification API: native.ts shows it (one
      // notification per chat, bundled under one summary, like WhatsApp).
      const detail: NativeNotice = {
        chat: locked ? "xmm-locked" : m.chat,
        id: m.id,
        title,
        body,
        sender: locked ? "xmm" : sender,
        chatName: locked ? "xmm" : S.displayName(m.chat),
        group: group && !locked,
        avatar: avatar ? new URL(avatar, document.baseURI).href : null,
        ts: m.ts,
      };
      window.dispatchEvent(new CustomEvent("xmm-notify", { detail }));
      return;
    }
    if (typeof Notification === "undefined" || Notification.permission !== "granted") return;
    void (async () => {
      // Through the service worker: the photo in a circle with the xmm logo,
      // one notification per chat (sw.js show(), shared with Web Push).
      const sw = (await navigator.serviceWorker?.getRegistration().catch(() => undefined))?.active;
      if (sw) {
        const n = { title, body, tag: locked ? "xmm-locked" : m.chat, avatar: avatar ?? "", name: locked ? "" : S.displayName(m.chat), key: locked ? "" : m.chat, group: group && !locked, chat: locked ? "" : m.chat, account: this.me, stack: true };
        return sw.postMessage({ type: "notify", n });
      }
      try {
        const n = new Notification(title, { body, tag: locked ? "xmm-locked" : m.chat, icon: await circleIcon(avatar), badge: "/icon-192.png" } as NotificationOptions);
        n.onclick = () => {
          window.focus();
          if (!locked) void this.openChat(m.chat);
          n.close();
        };
      } catch {
        /* some browsers only allow notifications from a service worker */
      }
    })();
  }

  private setTyping(chat: string, until: number, who?: string) {
    const cur = S.typing.value.get(chat) ?? 0;
    if (!until && !cur) return;
    const map = new Map(S.typing.value);
    if (until) map.set(chat, until);
    else map.delete(chat);
    S.typing.value = map;
    if (who) S.typingWho.value = new Map(S.typingWho.value).set(chat, who);
    if (until) setTimeout(() => S.isTyping(chat) || this.setTyping(chat, 0), until - Date.now() + 50);
  }

  // ---- local store --------------------------------------------------------

  /** Stores a message unless already known. Returns true if it was new. */
  private async insertMessage(msg: Msg): Promise<boolean> {
    const tx = this.db.transaction(["messages", "receipts", "kv"], "readwrite");
    const existing = await tx.objectStore("messages").get(msg.key);
    if (existing) {
      if (!existing.stanzaId && msg.stanzaId) await tx.objectStore("messages").put({ ...existing, stanzaId: msg.stanzaId });
      await tx.done;
      return false;
    }
    // Responses that arrived before this message: votes / RSVPs for a poll or
    // event, pins and keeps for any message.
    const pending = (await tx.objectStore("kv").get("responses")) as Record<string, Response[]> | undefined;
    const rs = pending?.[msg.key];
    if (rs) {
      for (const r of rs) msg = withResponse(msg, r) ?? msg;
      delete pending![msg.key];
      await tx.objectStore("kv").put(pending, "responses");
    }
    // …and reactions.
    const earlyR = (await tx.objectStore("kv").get("reactions:early")) as Record<string, Record<string, string | null>> | undefined;
    const er = earlyR?.[msg.key];
    if (er) {
      const reactions = { ...msg.reactions };
      for (const [who, e] of Object.entries(er)) {
        if (e) reactions[who] = e;
        else delete reactions[who];
      }
      msg = { ...msg, reactions };
      delete earlyR![msg.key];
      await tx.objectStore("kv").put(earlyR, "reactions:early");
    }
    // Opened on another of our devices already.
    if (msg.viewOnce && !msg.viewed && (((await tx.objectStore("kv").get("viewonce:opened")) as string[] | undefined) ?? []).includes(msg.key)) msg = viewedCopy(msg);
    // A receipt may have arrived before the message (archive order).
    const early = await tx.objectStore("receipts").get(msg.key);
    if (early && msg.dir === "out") {
      msg.status = Math.max(msg.status, early.status) as Status;
      await tx.objectStore("receipts").delete(msg.key);
    }
    await tx.objectStore("messages").put(msg);
    await tx.done;
    if (S.activeChat.value === msg.chat) S.messages.value = insertSorted(S.messages.value, msg);
    if (!msg.undecryptable) this.markBackupDirty();
    if (msg.pin) void this.refreshPins(msg.chat); // a pin that arrived before it
    return true;
  }

  private async applyStatus(chat: string, id: string, status: Status) {
    await this.applyStatusOne(chat, id, status);
    const m = await this.db.get("messages", msgKey(chat, id));
    if (m?.bcast) await this.aggregateBroadcast(m.bcast, id);
  }

  private async applyStatusOne(chat: string, id: string, status: Status) {
    const key = msgKey(chat, id);
    const msg = await this.db.get("messages", key);
    if (!msg) {
      if (status > Status.Sent) {
        const prev = await this.db.get("receipts", key);
        if (!prev || prev.status < status) await this.db.put("receipts", { key, status });
      }
      return;
    }
    if (msg.dir !== "out") return;
    const upgrade = status === Status.Failed ? msg.status === Status.Pending || msg.status === Status.Sent : status > msg.status;
    // A read marker for a message that's already read still covers the ones
    // before it that arrived late (sent earlier, delivered after it).
    if (!upgrade && status !== Status.Read) return;
    const changed = upgrade ? [{ ...msg, status }] : [];
    if (status === Status.Read) {
      // A read marker covers everything before it too.
      const range = IDBKeyRange.bound([chat, -Infinity], [chat, msg.ts], false, true);
      let cur = await this.db.transaction("messages").store.index("byChatTs").openCursor(range, "prev");
      while (cur) {
        const m = cur.value;
        if (m.dir === "out") {
          if (m.status === Status.Read) break;
          if (m.status >= Status.Sent) changed.push({ ...m, status: Status.Read });
        }
        cur = await cur.continue();
      }
    }
    const tx = this.db.transaction("messages", "readwrite");
    changed.forEach((m) => void tx.store.put(m));
    await tx.done;
    if (S.activeChat.value === chat) {
      const byKey = new Map(changed.map((m) => [m.key, m]));
      S.messages.value = S.messages.value.map((m) => byKey.get(m.key) ?? m);
    }
    const c = this.chatMap.get(chat);
    const last = c && changed.find((m) => m.id === c.lastId);
    if (c && last) this.saveChat({ ...c, lastStatus: last.status });
  }

  /** Our own read marker (from another device): clear unread up to id. */
  private async readUpTo(chat: string, id: string) {
    const c = this.chatMap.get(chat);
    if (!c) return;
    const marked = await this.db.get("messages", msgKey(chat, id));
    let unread = 0;
    if (marked) {
      const range = IDBKeyRange.bound([chat, marked.ts], [chat, Infinity], true, false);
      let cur = await this.db.transaction("messages").store.index("byChatTs").openCursor(range);
      while (cur) {
        if (cur.value.dir === "in") unread++;
        cur = await cur.continue();
      }
    }
    if (unread !== c.unread || c.lastReadId !== id) this.saveChat({ ...c, unread, lastReadId: id, mention: unread ? c.mention : false });
  }

  private touchChat(msg: Msg, unread: boolean, mention = false) {
    const c: Chat = this.chatMap.get(msg.chat) ?? { jid: msg.chat, lastBody: "", lastTs: 0, lastDir: "", lastStatus: Status.Pending, unread: 0 };
    const next = { ...c };
    if (msg.ts >= c.lastTs) {
      Object.assign(next, { lastBody: summarize(msg), lastTs: msg.ts, lastDir: msg.dir, lastStatus: msg.status, lastId: msg.id, lastFrom: msg.from });
    }
    if (unread) next.unread = c.unread + 1;
    if (mention) next.mention = true;
    this.saveChat(next);
  }

  private saveChat(c: Chat) {
    this.chatMap.set(c.jid, c);
    void this.db.put("chats", c);
    this.publishChats();
  }

  private publishChats() {
    S.chats.value = [...this.chatMap.values()].sort((a, b) => b.lastTs - a.lastTs);
  }

  private isViewing(chat: string) {
    return S.activeChat.value === chat && S.attentive.value;
  }

  // ---- archive sync (MAM) -------------------------------------------------

  private async mamQuery(opts: { with?: string; before?: string; after?: string; end?: number; max: number }) {
    const queryid = uuid();
    const fields = [xml("field", { var: "FORM_TYPE", type: "hidden" }, xml("value", {}, NS.mam))];
    if (opts.with) fields.push(xml("field", { var: "with" }, xml("value", {}, opts.with)));
    if (opts.end) fields.push(xml("field", { var: "end" }, xml("value", {}, new Date(opts.end).toISOString())));
    const set = xml(
      "set",
      { xmlns: NS.rsm },
      xml("max", {}, String(opts.max)),
      opts.before !== undefined ? xml("before", {}, opts.before) : null,
      opts.after ? xml("after", {}, opts.after) : null,
    );
    const iq = xml("iq", { type: "set" }, xml("query", { xmlns: NS.mam, queryid }, xml("x", { xmlns: NS.forms, type: "submit" }, ...fields), set));
    this.mam.set(queryid, []);
    try {
      const res = await this.xmpp.iqCaller.request(iq, 30_000);
      const fin = res.getChild("fin", NS.mam);
      const rsm = fin?.getChild("set", NS.rsm);
      return {
        items: this.mam.get(queryid)!,
        complete: fin?.attrs.complete === "true",
        first: rsm?.getChildText("first") ?? undefined,
        last: rsm?.getChildText("last") ?? undefined,
      };
    } finally {
      this.mam.delete(queryid);
    }
  }

  private async processArchived(items: ArchivedItem[]) {
    for (const it of items) await this.enqueue(() => this.route(it.message, { archived: true, stanzaId: it.stanzaId, ts: it.ts }));
  }

  /** Catch up with everything that happened while this device was away. */
  private async sync() {
    S.syncing.value = true;
    this.synced = false;
    try {
      let cursor = (await this.db.get("kv", "mam:cursor")) as string | undefined;
      if (cursor) {
        try {
          for (;;) {
            const r = await this.mamQuery({ after: cursor, max: 100 });
            await this.processArchived(r.items);
            if (r.last) {
              cursor = r.last;
              await this.db.put("kv", cursor, "mam:cursor");
            }
            if (r.complete || !r.items.length) break;
          }
        } catch (e) {
          // Cursor no longer in the archive (e.g. expired): start over.
          if (String(e).includes("item-not-found")) {
            await this.db.delete("kv", "mam:cursor");
            cursor = undefined;
          } else throw e;
        }
      }
      if (!cursor) {
        // New device: pull the most recent history, newest page first.
        const pages: ArchivedItem[][] = [];
        let before = "";
        let newest: string | undefined;
        for (let i = 0; i < 5; i++) {
          const r = await this.mamQuery({ before, max: 100 });
          pages.unshift(r.items);
          newest ??= r.last;
          if (r.complete || !r.first) break;
          before = r.first;
        }
        await this.processArchived(pages.flat());
        if (newest) await this.db.put("kv", newest, "mam:cursor");
      }
    } catch (e) {
      console.warn("mam sync", e);
    } finally {
      this.synced = true;
      S.syncing.value = false;
    }
  }

  // ---- outgoing -----------------------------------------------------------

  sendText(chat: string, body: string, extra: Omit<Draft, "body"> = {}) {
    return this.sendDraft(chat, { ...extra, body });
  }

  /** Stores the message locally (visible immediately), uploads media if
   * needed, then sends it. */
  async sendDraft(chat: string, d: Draft): Promise<Msg | undefined> {
    const body = d.body.trim();
    if (!body && !d.media && !d.contact && !d.location && !d.poll && !d.event) return;
    this.stopComposing(chat, false);
    const id = uuid();
    const msg: Msg = {
      key: msgKey(chat, id),
      id,
      chat,
      from: this.me,
      dir: "out",
      kind: d.kind ?? "text",
      body,
      ts: Date.now(),
      status: Status.Pending,
      media: d.media,
      replyTo: d.replyTo,
      preview: d.preview,
      forwarded: d.forwarded || undefined,
      mentions: d.mentions?.length ? d.mentions : undefined,
      contact: d.contact,
      location: d.location,
      viewOnce: d.viewOnce || undefined,
      poll: d.poll,
      event: d.event,
      sticker: d.sticker,
      statusRef: d.statusRef,
    };
    const ttl = S.chatTTL(chat);
    if (ttl) {
      msg.ttl = ttl;
      msg.expiresAt = msg.ts + ttl * 1000;
    }
    if (msg.replyTo) msg.replyTo = await this.snapshotQuote(chat, msg.replyTo);
    await this.enqueue(async () => {
      await this.insertMessage(msg);
      this.touchChat(msg, false);
    });
    void this.bumpFrequent(chat);
    if (!d.quiet) playSent();
    await this.deliver(msg);
    if (msg.location?.live) {
      const live = ((await this.db.get("kv", "live")) as string[] | undefined) ?? [];
      await this.db.put("kv", [...live, msg.key], "live");
      this.startLive(msg);
    }
    return msg;
  }

  // ---- frequently contacted -------------------------------------------------------
  // A score per chat: +1 per message you send, halving every two weeks, so
  // the list follows who you talk to now.

  private static readonly HALF_LIFE = 14 * 86_400_000;

  private async bumpFrequent(chat: string) {
    if (S.isChannel(chat) || S.isBroadcast(chat) || chat === this.me) return;
    const now = Date.now();
    const f = ((await this.db.get("kv", "frequent")) as Record<string, { n: number; t: number }> | undefined) ?? {};
    const cur = f[chat];
    f[chat] = { n: (cur ? cur.n * 0.5 ** ((now - cur.t) / Engine.HALF_LIFE) : 0) + 1, t: now };
    await this.db.put("kv", f, "frequent");
    this.publishFrequent(f);
  }

  private publishFrequent(f: Record<string, { n: number; t: number }>) {
    const now = Date.now();
    S.frequent.value = Object.entries(f)
      .map(([j, e]) => [j, e.n * 0.5 ** ((now - e.t) / Engine.HALF_LIFE)] as const)
      .filter(([, s]) => s > 0.3)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([j]) => j);
  }

  private uploading = new Map<string, AbortController>();

  /** Upload (if needed) and transmit. Failures leave the message retryable. */
  private async deliver(m: Msg) {
    if (m.media && !m.media.url) {
      if (this.uploading.has(m.key)) return;
      const blob = m.media.blob;
      if (!blob) return this.markFailed(m);
      const ctl = new AbortController();
      this.uploading.set(m.key, ctl);
      this.setProgress(m.key, 0);
      try {
        // Encrypt before upload: the server stores ciphertext only.
        // Channels are public (like WhatsApp's): their media isn't encrypted.
        const sealed = E2E_ON && !S.isChannel(m.chat) ? await (await e2eModule()).E2E.encryptBlob(blob) : null;
        const up = await api.uploadWithProgress(sealed?.blob ?? blob, sealed ? "encrypted.bin" : (m.media.name ?? "file"), (f) => this.setProgress(m.key, f), ctl.signal);
        const updated = await this.updateMessage(m.key, (x) => ({
          ...x,
          media: sealed
            ? { ...x.media!, url: up.url, key: sealed.key, iv: sealed.iv, sha256: sealed.sha256, blob: undefined }
            : { ...x.media!, url: up.url, mime: up.mime, size: up.size, blob: undefined },
        }));
        if (!updated) return;
        m = updated;
      } catch (e) {
        console.warn("upload", e);
        return this.markFailed(m);
      } finally {
        this.uploading.delete(m.key);
        this.setProgress(m.key, null);
      }
    }
    if (m.status === Status.Failed) return;
    await this.transmit(m);
  }

  private setProgress(key: string, f: number | null) {
    const map = new Map(S.uploads.value);
    if (f === null) map.delete(key);
    else map.set(key, f);
    S.uploads.value = map;
  }

  private async markFailed(m: Msg) {
    await this.updateMessage(m.key, (x) => (x.status === Status.Pending ? { ...x, status: Status.Failed } : null));
  }

  cancelUpload(key: string) {
    this.uploading.get(key)?.abort();
  }

  private async transmit(m: Msg) {
    if (S.isChannel(m.chat)) return this.transmitPost(m);
    if (S.isBroadcast(m.chat)) return this.transmitBroadcast(m);
    if (!E2E_ON) return this.send(buildStanza(m));
    if (this.xmpp.status !== "online") return; // resent after reconnect
    clearTimeout(this.retryTimers.get(m.key));
    try {
      await this.sendEncrypted(m.chat, m.id, "msg", contentPayload(m), [
        xml("request", { xmlns: NS.receipts }),
        xml("markable", { xmlns: NS.markers }),
        xml("origin-id", { xmlns: NS.sid, id: m.id }),
        xml("active", { xmlns: NS.chatstates }),
      ]);
      this.retryCounts.delete(m.key);
    } catch (e) {
      if (e instanceof (await e2eModule()).NoDevicesError) {
        // Recipient isn't logged in anywhere yet (or is publishing keys right
        // now): keep it queued (clock icon), retrying 5s, 10s, 20s, then 30s.
        const n = this.retryCounts.get(m.key) ?? 0;
        this.retryCounts.set(m.key, n + 1);
        this.retryTimers.set(m.key, window.setTimeout(() => void this.transmit(m), Math.min(30_000, 5_000 * 2 ** n)));
        return;
      }
      console.warn("encrypt", e);
      await this.markFailed(m);
    }
  }

  /** After a reconnect, send whatever never got a server ack. */
  private async resendPending() {
    const pending = await this.db.getAllFromIndex("messages", "byStatus", Status.Pending);
    pending.sort((a, b) => a.ts - b.ts);
    for (const m of pending) if (m.dir === "out" && !m.hidden) void this.deliver(m);
  }

  async retry(chat: string, id: string) {
    const next = await this.updateMessage(msgKey(chat, id), (m) => (m.status === Status.Failed ? { ...m, status: Status.Pending } : null));
    if (next) await this.deliver(next);
  }

  // ---- reactions, edits, deletes, forwards ----------------------------------

  async react(m: Msg, emoji: string | null) {
    if (S.isChannel(m.chat)) return this.reactPost(m, emoji);
    const mine = m.reactions?.[this.me];
    const next = emoji && emoji !== mine ? emoji : undefined; // same emoji again = remove
    await this.enqueue(() => this.applyReaction(m.chat, this.me, m.id, next));
    await this.send(
      xml(
        "message",
        { to: m.chat, type: msgType(m.chat), id: uuid() },
        xml("reactions", { xmlns: NS.reactions, id: m.id }, next ? xml("reaction", {}, next) : null),
        xml("store", { xmlns: NS.hints }),
      ),
    );
  }

  canEdit(m: Msg) {
    if (S.isChannel(m.chat)) return this.isChannelAdmin(m.chat) && !m.deleted && m.status !== Status.Pending;
    return m.dir === "out" && !m.deleted && !m.forwarded && m.kind !== "voice" && m.kind !== "poll" && m.kind !== "event" && Date.now() - m.ts < EDIT_WINDOW && m.status !== Status.Failed;
  }

  canRetract(m: Msg) {
    if (S.isChannel(m.chat)) return this.isChannelAdmin(m.chat) && !m.deleted && m.status !== Status.Pending;
    return m.dir === "out" && !m.deleted && Date.now() - m.ts < RETRACT_WINDOW && m.status !== Status.Failed && m.status !== Status.Pending;
  }

  async edit(m: Msg, body: string, preview?: LinkPreview) {
    body = body.trim();
    if (!this.canEdit(m) || (!body && m.kind === "text") || body === m.body) return;
    const ts = Date.now();
    if (S.isChannel(m.chat)) {
      await api.editPost(m.chat, m.id, body); // the channel's echo updates every device
      return;
    }
    await this.enqueue(() =>
      this.updateMessage(m.key, (x) => ({ ...x, body, preview, editedAt: ts })),
    );
    if (E2E_ON) {
      await this.sendEncrypted(m.chat, uuid(), "edit", { op: "edit", id: m.id, body, preview }).catch((e) => console.warn("edit", e));
      return;
    }
    await this.send(
      xml(
        "message",
        { to: m.chat, type: msgType(m.chat), id: uuid() },
        xml("body", {}, body),
        xml("replace", { xmlns: NS.correct, id: m.id }),
        preview ? previewEl(preview) : null,
        xml("store", { xmlns: NS.hints }),
      ),
    );
  }

  async retract(m: Msg) {
    if (!this.canRetract(m)) return;
    if (S.isChannel(m.chat)) {
      await api.deletePost(m.chat, m.id);
      return;
    }
    if (this.liveWatches.has(m.key)) await this.stopLive(m.key);
    await this.enqueue(() => this.applyRetract(m.chat, this.me, m.id));
    await this.send(
      xml(
        "message",
        { to: m.chat, type: msgType(m.chat), id: uuid() },
        xml("retract", { xmlns: NS.retract, id: m.id }),
        xml("fallback", { xmlns: NS.fallback, for: NS.retract }),
        xml("body", {}, "This person attempted to delete a previous message, but it's unsupported by your client."),
        xml("store", { xmlns: NS.hints }),
      ),
    );
  }

  /** Messages with a file, for Manage storage (without their local blobs). */
  async mediaMessages(): Promise<Msg[]> {
    const out: Msg[] = [];
    let cur = await this.db.transaction("messages").store.openCursor();
    while (cur) {
      const m = cur.value;
      if (m.media?.url && !m.hidden && !m.deleted && m.kind !== "sticker") out.push({ ...m, media: { ...m.media, blob: undefined } });
      cur = await cur.continue();
    }
    return out;
  }

  /** Delete for me: hide locally, keep a tombstone so sync does not revive it. */
  async hide(m: Msg) {
    await this.enqueue(() => this.hideNow(m));
  }

  private async hideNow(m: Msg) {
    {
      if (m.status === Status.Pending) this.cancelUpload(m.key);
      await this.db.put("messages", { ...m, hidden: true, body: "", media: undefined, preview: undefined });
      this.markBackupDirty();
      S.messages.value = S.messages.value.filter((x) => x.key !== m.key);
      const c = this.chatMap.get(m.chat);
      if (c?.lastId === m.id) {
        const [last] = (await messagesPage(this.db, m.chat, 1)).slice(-1);
        this.saveChat({
          ...c,
          lastId: last?.id,
          lastBody: last ? summarize(last) : "",
          lastDir: last?.dir ?? "",
          lastStatus: last?.status ?? Status.Pending,
          lastTs: last?.ts ?? c.lastTs,
        });
      }
    }
  }

  async forward(msgs: Msg[], to: string[]) {
    for (const chat of to)
      for (const m of msgs) {
        if (m.deleted) continue;
        await this.sendDraft(chat, {
          body: m.body,
          kind: m.kind,
          media: m.media ? { ...m.media, blob: m.media.url ? undefined : m.media.blob } : undefined,
          preview: m.preview,
          contact: m.contact,
          location: m.location ? { lat: m.location.lat, lon: m.location.lon, accuracy: m.location.accuracy, name: m.location.name } : undefined,
          sticker: m.sticker,
          // A fresh poll / event: nobody's answers come along.
          poll: m.poll ? { question: m.poll.question, options: m.poll.options, multi: m.poll.multi } : undefined,
          event: m.event ? { ...m.event, rsvps: undefined, canceled: undefined } : undefined,
          forwarded: true,
        });
      }
  }

  /** Called on every keystroke in the composer. */
  composing(chat: string) {
    const timer = this.composeTimers.get(chat);
    if (timer === undefined) this.sendChatState(chat, "composing");
    clearTimeout(timer);
    this.composeTimers.set(chat, window.setTimeout(() => this.stopComposing(chat, true), 4000));
  }

  stopComposing(chat: string, notify: boolean) {
    const timer = this.composeTimers.get(chat);
    if (timer === undefined) return;
    clearTimeout(timer);
    this.composeTimers.delete(chat);
    if (notify) this.sendChatState(chat, "paused");
  }

  private sendChatState(chat: string, state: "composing" | "paused") {
    if (S.isChannel(chat) || S.isBroadcast(chat)) return;
    void this.send(xml("message", { to: chat, type: msgType(chat) }, xml(state, { xmlns: NS.chatstates }), xml("no-store", { xmlns: NS.hints })));
  }

  /** Send read markers for what the user is looking at. In 1:1 chats one
   * marker for the newest incoming message; in groups one per author (read
   * receipts go privately to each author, not to the whole group). */
  /** The open chat was read up to its newest message. `arrived`: a message
   * that just came in; one that lands *behind* what was already read (sent
   * earlier but delayed, e.g. while the sender fetched our keys) gets its
   * own read marker, or the sender would never see it as read. */
  private async markRead(chat: string, arrived?: Msg, force = false) {
    if (!force && !this.isViewing(chat)) return;
    const c = this.chatMap.get(chat);
    if (!c) return;
    // Unreadable messages get no read marker (it would count as delivered).
    const recent = (await messagesPage(this.db, chat, 50)).filter((m) => m.dir === "in" && m.kind !== "system" && !m.undecryptable);
    const lastIn = recent[recent.length - 1];
    if (!lastIn) {
      if (c.unread || c.mention) this.saveChat({ ...c, unread: 0, mention: false });
      return;
    }
    if (arrived && arrived.dir === "in" && !arrived.undecryptable && arrived.kind !== "system" && arrived.id !== lastIn.id && c.lastReadId && arrived.ts <= lastIn.ts)
      await this.sendDisplayed(chat, arrived);
    if (c.lastReadId === lastIn.id && !c.unread) return;
    this.saveChat({ ...c, unread: 0, mention: false, lastReadId: lastIn.id });
    if (S.isChannel(chat) || S.isBroadcast(chat)) return; // no read markers
    if (!S.isGroup(chat)) return this.sendDisplayed(chat, lastIn);
    const since = recent.find((m) => m.id === c.lastReadId)?.ts ?? 0;
    const newestBy = new Map<string, Msg>();
    for (const m of recent) if (m.ts > since) newestBy.set(m.from, m);
    for (const [author, m] of newestBy)
      await this.send(
        xml(
          "message",
          { to: author, type: "chat", id: uuid() },
          xml("displayed", { xmlns: NS.markers, id: m.id }),
          xml("group", { xmlns: NS.group, jid: chat }),
          xml("store", { xmlns: NS.hints }),
        ),
      );
  }


  /** "Mark as read" (or a reply) from a notification: read without opening the chat. */
  readChat(chat: string) {
    return this.enqueue(() => this.markRead(chat, undefined, true));
  }

  /** A read marker for one message (1:1: to the chat; group: to its author). */
  private async sendDisplayed(chat: string, m: Msg) {
    if (S.isChannel(chat) || S.isBroadcast(chat)) return;
    if (S.isGroup(chat)) {
      await this.send(
        xml(
          "message",
          { to: m.from, type: "chat", id: uuid() },
          xml("displayed", { xmlns: NS.markers, id: m.id }),
          xml("group", { xmlns: NS.group, jid: chat }),
          xml("store", { xmlns: NS.hints }),
        ),
      );
      return;
    }
    // Read receipts off: the sender gets no blue ticks, but our other
    // devices still learn the chat was read (marker addressed to ourselves).
    const to = S.privacy.value.read_receipts ? chat : this.me;
    await this.send(
      xml(
        "message",
        { to, type: "chat", id: uuid() },
        xml("displayed", { xmlns: NS.markers, id: m.id }),
        to === this.me ? xml("chat", { xmlns: NS.self, jid: chat }) : null,
        xml("store", { xmlns: NS.hints }),
      ),
    );
  }

  // ---- UI actions ---------------------------------------------------------

  async openChat(chat: string | null, focus?: Msg) {
    if (S.activeChat.value && S.activeChat.value !== chat) this.stopComposing(S.activeChat.value, true);
    S.activeChat.value = chat;
    S.focusMessage.value = focus?.key ?? null;
    S.pinned.value = [];
    if (!chat) {
      S.messages.value = [];
      return;
    }
    // Jumping to an older message (search, starred): load from a bit before
    // it up to now so the view is continuous.
    let page = await messagesPage(this.db, chat, PAGE);
    if (focus && page.length && focus.ts < page[0].ts) {
      const before = await messagesPage(this.db, chat, 20, focus.ts);
      page = [...before, ...(await messagesSince(this.db, chat, focus.ts, 1000))];
    }
    if (S.activeChat.value !== chat) return;
    S.messages.value = page;
    S.hasOlder.value = true;
    void this.queryLastSeen(chat);
    void this.ensureProfiles([chat]);
    void this.refreshPins(chat);
    await this.enqueue(() => this.markRead(chat));
  }

  /** Scroll-back: older messages from the local store, then the archive. */
  async loadOlder(): Promise<void> {
    const chat = S.activeChat.value;
    const oldest = S.messages.value[0];
    if (!chat || !S.hasOlder.value) return;
    let page = await messagesPage(this.db, chat, PAGE, oldest?.ts ?? Infinity);
    if (page.length < PAGE && this.xmpp.status === "online") {
      try {
        const localOldest = page[0]?.ts ?? oldest?.ts;
        const r = await this.mamQuery({ with: chat, before: "", end: localOldest ? localOldest - 1 : undefined, max: PAGE });
        await this.processArchived(r.items);
        if (r.complete || !r.items.length) S.hasOlder.value = false;
        page = await messagesPage(this.db, chat, PAGE, oldest?.ts ?? Infinity);
      } catch (e) {
        console.warn("load older", e);
      }
    } else if (page.length < PAGE) {
      S.hasOlder.value = false;
    }
    if (S.activeChat.value !== chat) return;
    const known = new Set(S.messages.value.map((m) => m.key));
    S.messages.value = [...page.filter((m) => !known.has(m.key)), ...S.messages.value];
  }

  /** Full-text search over this device's messages (newest first). */
  async search(q: string, limit = 50): Promise<Msg[]> {
    const needle = q.trim().toLowerCase();
    if (needle.length < 2) return [];
    const out: Msg[] = [];
    let cur = await this.db.transaction("messages").store.openCursor();
    while (cur) {
      const m = cur.value;
      if (!m.hidden && !m.deleted && m.kind !== "system" && (m.body.toLowerCase().includes(needle) || m.media?.name?.toLowerCase().includes(needle))) out.push(m);
      cur = await cur.continue();
    }
    return out.sort((a, b) => b.ts - a.ts).slice(0, limit);
  }

  getMessage(key: string) {
    return this.db.get("messages", key);
  }

  // ---- chat settings & stars (synced through the backend) --------------------

  async loadChatSettings() {
    try {
      const { chats } = await api.chatSettings();
      S.chatSettings.value = new Map(chats.map((c) => [c.jid, c]));
      await this.db.put("kv", chats, "chatSettings");
    } catch (e) {
      console.warn("chat settings", e);
    }
  }

  async setChat(jid: string, patch: { pinned?: boolean; archived?: boolean; muted_until?: number }) {
    const prev = S.chatSettings.value.get(jid) ?? { jid, pinned_at: null, archived: false, muted_until: null };
    const next = { ...prev };
    if (patch.pinned !== undefined) next.pinned_at = patch.pinned ? Math.floor(Date.now() / 1000) : null;
    if (patch.archived !== undefined) next.archived = patch.archived;
    if (patch.muted_until !== undefined)
      next.muted_until = patch.muted_until === 0 ? null : patch.muted_until < 0 ? 253402214400 : patch.muted_until;
    S.chatSettings.value = new Map(S.chatSettings.value).set(jid, next); // optimistic
    try {
      await api.patchChat(jid, patch);
    } catch (e) {
      S.chatSettings.value = new Map(S.chatSettings.value).set(jid, prev);
      throw e;
    }
  }

  // ---- push notifications (XEP-0357) ---------------------------------------------------

  /** Subscribe this browser to Web Push and tell ejabberd to notify our
   * push app server when this device is offline. Needs notification
   * permission and the service worker. */
  // ---- history (QR linking, backups) ----------------------------------------------------

  /** History for another device: the last 30 days (up to 10,000 messages). */
  exportHistory() {
    return exportHistory(this.db, this.me, { days: HISTORY_DAYS, limit: 10_000 });
  }

  /** This device has the whole history, not just the last 30 days. */
  hasFullHistory() {
    return this.db.get("kv", "history:full");
  }

  /** Everything: for the phone app, and the password-protected backup. */
  exportAllHistory() {
    return exportHistory(this.db, this.me, { limit: 200_000 });
  }

  /** Ask my other devices for the chat history again (placeholder button). */
  async requestHistoryAgain() {
    await this.db.delete("kv", "history:done");
    await this.db.delete("kv", "history:key");
    await this.syncHistory();
  }

  /** Stop asking (e.g. no other device left): placeholders stay. */
  async skipHistory() {
    await this.db.put("kv", true, "history:done");
    S.historyState.value = "declined";
  }

  async importHistory(b: Bundle) {
    const n = await this.enqueue(() => importHistory(this.db, b, this.me));
    this.chatMap.clear();
    for (const c of await this.db.getAll("chats")) this.chatMap.set(c.jid, c);
    this.publishChats();
    const active = S.activeChat.value;
    if (active) await this.openChat(active);
    return n;
  }

  async enablePush(subscription?: PushSubscriptionJSON | import("./api").NativePushToken) {
    if (!subscription && isNative) {
      // The app registers with FCM/APNs itself (native.ts) and calls back here.
      if (this.nativePush) subscription = this.nativePush;
      else return;
    }
    if (!subscription) {
      if (typeof Notification === "undefined" || Notification.permission !== "granted" || !("serviceWorker" in navigator)) return;
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg?.pushManager) return;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const { public_key } = await api.pushVapid();
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToBytes(public_key) });
      }
      subscription = sub.toJSON();
    }
    const { jid, node, secret } = await api.putPush(subscription);
    await this.xmpp.iqCaller.request(
      xml(
        "iq",
        { type: "set" },
        xml(
          "enable",
          { xmlns: NS.push, jid, node },
          xml(
            "x",
            { xmlns: NS.forms, type: "submit" },
            xml("field", { var: "FORM_TYPE" }, xml("value", {}, "http://jabber.org/protocol/pubsub#publish-options")),
            xml("field", { var: "secret" }, xml("value", {}, secret)),
          ),
        ),
      ),
    );
    S.pushEnabled.value = true;
  }

  /** Native app: the device's FCM/APNs token (re-registered on every connect). */
  nativePush: import("./api").NativePushToken | null = null;

  // ---- blocking (XEP-0191, enforced by the server) --------------------------------

  async loadBlocklist() {
    try {
      const res = await this.xmpp.iqCaller.request(xml("iq", { type: "get" }, xml("blocklist", { xmlns: NS.blocking })));
      const set = new Set(res.getChild("blocklist", NS.blocking)?.getChildren("item").map((i) => bare(i.attrs.jid)) ?? []);
      S.blocked.value = set;
      await this.db.put("kv", [...set], "blocked");
    } catch (e) {
      console.warn("blocklist", e);
    }
  }

  async setBlocked(jid: string, block: boolean) {
    await this.xmpp.iqCaller.request(xml("iq", { type: "set" }, xml(block ? "block" : "unblock", { xmlns: NS.blocking }, xml("item", { jid }))));
    const set = new Set(S.blocked.value);
    if (block) set.add(jid);
    else set.delete(jid);
    S.blocked.value = set;
    await this.db.put("kv", [...set], "blocked");
  }

  // ---- disappearing messages ---------------------------------------------------------

  async loadTimers() {
    try {
      const { timers } = await api.timers();
      S.timers.value = new Map(timers.map((t) => [t.jid, t.ttl]));
      await this.db.put("kv", [...S.timers.value], "timers");
    } catch (e) {
      console.warn("timers", e);
    }
  }

  async setTimer(jid: string, ttl: number) {
    if (S.isGroup(jid)) {
      await api.setTimer(jid, ttl); // the server posts the group notice
      await this.loadGroup(jid);
      return;
    }
    await api.setTimer(jid, ttl);
    S.timers.value = new Map(S.timers.value).set(jid, ttl);
    await this.db.put("kv", [...S.timers.value], "timers");
    const id = uuid();
    await this.enqueue(() => this.systemNotice(jid, "dis-" + id, "disappearing", this.me, String(ttl)));
    await this.send(
      xml(
        "message",
        { to: jid, type: "chat", id },
        xml("disappearing", { xmlns: NS.disappear, ttl }),
        xml("body", {}, ttl ? "Disappearing messages were turned on." : "Disappearing messages were turned off."),
        xml("store", { xmlns: NS.hints }),
      ),
    );
  }

  private async systemNotice(chat: string, id: string, type: string, actor: string, value: string, ts = Date.now()) {
    const msg: Msg = {
      key: msgKey(chat, id),
      id,
      chat,
      from: "system",
      dir: "in",
      kind: "system",
      body: "",
      ts,
      status: Status.Pending,
      system: { type, actor, targets: [], value },
    };
    if (await this.insertMessage(msg)) this.touchChat(msg, false);
  }

  /** Delete messages whose disappearing timer ran out. */
  private async sweepExpired() {
    const expired = await this.db.getAllFromIndex("messages", "byExpiry", IDBKeyRange.upperBound(Date.now()));
    for (const m of expired) if (!m.hidden) await this.hideNow(m);
  }

  /** A view-once photo/video was opened: wipe it from this device, and tell
   * our other devices to wipe it too (it can be opened once, not once per
   * device). */
  async markViewed(m: Msg) {
    await this.enqueue(() => this.wipeViewOnce(m.key));
    void this.send(
      xml("message", { to: this.me, type: "chat", id: uuid() }, xml("viewed", { xmlns: NS.self, id: m.id }), xml("chat", { xmlns: NS.self, jid: m.chat }), xml("store", { xmlns: NS.hints })),
    ).catch((e) => console.warn("view-once sync", e));
  }

  private async wipeViewOnce(key: string) {
    // Remembered, in case the message itself only arrives later (archive order).
    const opened = ((await this.db.get("kv", "viewonce:opened")) as string[] | undefined) ?? [];
    if (!opened.includes(key)) await this.db.put("kv", [...opened.slice(-999), key], "viewonce:opened");
    await this.updateMessage(key, (x) => (x.viewOnce && !x.viewed ? viewedCopy(x) : null));
  }

  async loadPrivacy() {
    try {
      S.privacy.value = await api.privacy();
      await this.db.put("kv", S.privacy.value, "privacy");
    } catch (e) {
      console.warn("privacy", e);
    }
  }

  async setPrivacy(p: Partial<Privacy>) {
    const prev = S.privacy.value;
    S.privacy.value = { ...prev, ...p }; // optimistic
    try {
      S.privacy.value = await api.patchPrivacy(p);
      await this.db.put("kv", S.privacy.value, "privacy");
    } catch (e) {
      S.privacy.value = prev;
      throw e;
    }
  }

  async loadStars() {
    try {
      const { stars } = await api.stars();
      S.stars.value = new Set(stars.map((x) => msgKey(x.chat, x.id)));
      await this.db.put("kv", stars, "stars");
    } catch (e) {
      console.warn("stars", e);
    }
  }

  async toggleStar(m: Msg) {
    const on = !S.stars.value.has(m.key);
    const set = new Set(S.stars.value);
    if (on) set.add(m.key);
    else set.delete(m.key);
    S.stars.value = set;
    try {
      await api.star(m.chat, m.id, on);
    } catch (e) {
      void this.loadStars();
      throw e;
    }
  }

  async starredMessages(): Promise<Msg[]> {
    const out: Msg[] = [];
    for (const key of S.stars.value) {
      const m = await this.db.get("messages", key);
      if (m && !m.hidden && !m.deleted) out.push(m);
    }
    return out.sort((a, b) => b.ts - a.ts);
  }

  saveDraft(chat: string, draft: string) {
    const c = this.chatMap.get(chat);
    if (c && (c.draft ?? "") !== draft) this.saveChat({ ...c, draft });
  }

  draft(chat: string) {
    return this.chatMap.get(chat)?.draft ?? "";
  }

  async deleteChat(chat: string) {
    if (S.isBroadcast(chat)) {
      await api.deleteBroadcast(chat.slice(2, chat.indexOf("@")));
      const next = new Map(S.broadcasts.value);
      next.delete(chat);
      S.broadcasts.value = next;
    }
    const tx = this.db.transaction(["messages", "chats"], "readwrite");
    let cur = await tx.objectStore("messages").index("byChatTs").openCursor(IDBKeyRange.bound([chat, -Infinity], [chat, Infinity]));
    while (cur) {
      await cur.delete();
      cur = await cur.continue();
    }
    await tx.objectStore("chats").delete(chat);
    await tx.done;
    // The backup drops this chat's messages up to now, too.
    const cleared = ((await this.db.get("kv", "backup:cleared")) as Record<string, number> | undefined) ?? {};
    await this.db.put("kv", { ...cleared, [chat]: Date.now() }, "backup:cleared");
    this.markBackupDirty();
    this.chatMap.delete(chat);
    this.publishChats();
    if (S.activeChat.value === chat) await this.openChat(null);
  }
}

// ---- helpers --------------------------------------------------------------

type Response = { op: "vote" | "rsvp" | "event-cancel" | "pin" | "keep"; from: string; ts: number; choices?: unknown; answer?: unknown; until?: number; kept?: boolean };

/** A poll/event with one more answer applied (null: nothing changes). The
 * newest answer of each person wins; only the organizer can cancel. */
function withResponse(m: Msg, r: Response): Msg | null {
  if (m.deleted) return null;
  if (r.op === "vote" && m.kind === "poll" && m.poll) {
    if ((m.poll.votes?.[r.from]?.ts ?? 0) >= r.ts) return null;
    const ids = new Set(m.poll.options.map((o) => o.id));
    let choices = Array.isArray(r.choices) ? [...new Set(r.choices.filter((c): c is string => typeof c === "string" && ids.has(c)))] : [];
    if (!m.poll.multi) choices = choices.slice(0, 1);
    return { ...m, poll: { ...m.poll, votes: { ...m.poll.votes, [r.from]: { choices, ts: r.ts } } } };
  }
  if (r.op === "rsvp" && m.kind === "event" && m.event && (r.answer === "going" || r.answer === "maybe" || r.answer === "no")) {
    if ((m.event.rsvps?.[r.from]?.ts ?? 0) >= r.ts) return null;
    return { ...m, event: { ...m.event, rsvps: { ...m.event.rsvps, [r.from]: { answer: r.answer, ts: r.ts } } } };
  }
  if (r.op === "event-cancel" && m.kind === "event" && m.event && !m.event.canceled && r.from === m.from) return { ...m, event: { ...m.event, canceled: true } };
  if (r.op === "pin" && m.kind !== "system") {
    if ((m.pin?.ts ?? 0) >= r.ts) return null;
    const until = Math.min(Number(r.until) || 0, r.ts + 31 * 86_400_000);
    return { ...m, pin: until > Date.now() ? { until, by: r.from, ts: r.ts } : undefined };
  }
  if (r.op === "keep" && m.ttl) {
    if ((m.kept?.ts ?? 0) >= r.ts) return null;
    // Anyone keeps it; only its sender or whoever kept it lets it go again.
    if (!r.kept && m.kept && r.from !== m.from && r.from !== m.kept.by) return null;
    return { ...m, kept: r.kept ? { by: r.from, ts: r.ts } : undefined, expiresAt: r.kept ? undefined : m.ts + m.ttl * 1000 };
  }
  return null;
}

/** A view-once photo/video once opened: the media is gone. */
function viewedCopy(m: Msg): Msg {
  return { ...m, viewed: true, body: "", media: m.media ? { mime: m.media.mime, size: 0 } : undefined };
}

function rosterItem(it: Element): S.RosterItem {
  return {
    jid: it.attrs.jid ?? "",
    name: it.attrs.name ?? "",
    subscription: (it.attrs.subscription ?? "none") as S.RosterItem["subscription"],
  };
}

function insertSorted(list: Msg[], m: Msg): Msg[] {
  if (list.some((x) => x.key === m.key)) return list;
  const out = list.slice();
  let i = out.length;
  while (i > 0 && out[i - 1].ts > m.ts) i--;
  out.splice(i, 0, m);
  return out;
}

function wsURL(u: string): string {
  if (/^wss?:\/\//.test(u)) return u;
  const base = new URL(baseURL()); // the server (native app) or this page
  const proto = base.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${base.host}${u.startsWith("/") ? u : "/" + u}`;
}

// ---- wire format ------------------------------------------------------------

function buildStanza(m: Msg): Element {
  const media = m.media;
  // Other XMPP clients see the caption, the file URL (XEP-0066 style), a
  // contact line or a geo: URI (RFC 5870).
  const body =
    m.body ||
    media?.url ||
    (m.contact ? `${m.contact.name}: ${m.contact.phone}` : "") ||
    (m.location ? `geo:${m.location.lat.toFixed(6)},${m.location.lon.toFixed(6)}` : "");
  return xml(
    "message",
    { to: m.chat, type: msgType(m.chat), id: m.id },
    xml("body", {}, body),
    media?.url
      ? [
          xml(
            "media",
            {
              xmlns: NS.media,
              kind: m.kind,
              url: media.url,
              mime: media.mime,
              size: media.size,
              name: media.name,
              width: media.width,
              height: media.height,
              duration: media.duration?.toFixed(2),
            },
            media.thumb ? xml("thumb", {}, media.thumb) : null,
            media.waveform ? xml("waveform", {}, media.waveform.join(",")) : null,
          ),
          xml("x", { xmlns: NS.oob }, xml("url", {}, media.url)),
        ]
      : null,
    m.replyTo ? xml("reply", { xmlns: NS.reply, to: m.replyTo.from, id: m.replyTo.id }) : null,
    m.preview ? previewEl(m.preview) : null,
    m.forwarded ? xml("fwd", { xmlns: NS.fwd }) : null,
    m.contact ? xml("contact", { xmlns: NS.contact, name: m.contact.name, phone: m.contact.phone, jid: m.contact.jid }) : null,
    m.location ? geolocEl(m.location) : null,
    m.location?.live ? xml("live", { xmlns: NS.live, until: m.location.live.until }) : null,
    (m.mentions ?? []).map((x) => xml("reference", { xmlns: NS.reference, type: "mention", uri: "xmpp:" + x.jid, begin: x.begin, end: x.end })),
    xml("request", { xmlns: NS.receipts }),
    xml("markable", { xmlns: NS.markers }),
    xml("origin-id", { xmlns: NS.sid, id: m.id }),
    xml("active", { xmlns: NS.chatstates }),
  );
}

const STATUS_BGS = ["#128c7e", "#34b7f1", "#8e44ad", "#c0392b", "#d35400", "#2c3e50", "#16a085", "#7f8c8d"];

function statusView(it: StatusItem, c: Record<string, unknown>, viewed: boolean): S.StatusView {
  const v: S.StatusView = { id: it.id, owner: it.owner, kind: it.kind, createdAt: it.created_at, expiresAt: it.expires_at, viewed };
  if (Array.isArray(c.mentions)) {
    const ms = c.mentions.filter((j): j is string => typeof j === "string" && /^[^@\s/]+@[^@\s/]+$/.test(j)).slice(0, 20);
    if (ms.length) v.mentions = ms;
  }
  if (typeof it.reaction === "string") v.reaction = it.reaction.slice(0, 32);
  if (it.kind === "text") {
    v.text = str(c.text, 700);
    v.bg = STATUS_BGS.includes(c.bg as string) ? (c.bg as string) : STATUS_BGS[0];
    v.font = Math.max(0, Math.min(3, Number(c.font) || 0));
  } else {
    const md = c.media as Record<string, unknown> | undefined;
    const url = mediaURL(typeof md?.url === "string" ? md.url : undefined);
    v.caption = str(c.caption, 700);
    if (md && url)
      v.media = {
        url,
        key: b64s(md.key),
        iv: b64s(md.iv),
        sha256: b64s(md.sha256),
        mime: str(md.mime, 100) || (it.kind === "video" ? "video/mp4" : "image/jpeg"),
        width: numv(md.width),
        height: numv(md.height),
        duration: numv(md.duration),
        thumb: safeThumb(typeof md.thumb === "string" ? md.thumb : undefined),
      };
  }
  return v;
}

/** When a message was sent, by the sender's clock: what chats are ordered
 * by (a message delayed on the way — e.g. while the sender fetched our
 * keys — keeps its place, and both sides agree on what "read up to here"
 * covers). Clamped to the arrival time: never in the future, at most a
 * week earlier. */
function sentTime(sent: unknown, arrived: number): number {
  const t = typeof sent === "number" && Number.isFinite(sent) ? sent : NaN;
  if (Number.isNaN(t)) return arrived;
  return Math.min(arrived, Math.max(t, arrived - 7 * 86_400_000));
}

function fmtDuration(s: number) {
  const m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}` : `${m}:${String(s % 60).padStart(2, "0")}`;
}

/** Chat entry for a call: a 1:1 <call-log/> or a group <call-invite/>. */
function callContent(el: Element, group: boolean): Content {
  const results: CallResult[] = ["answered", "missed", "declined", "busy", "failed", "ended"];
  const result = results.find((r) => r === el.attrs.result);
  return {
    kind: "call",
    body: "",
    call: {
      sid: String(el.attrs.id ?? "").slice(0, 64),
      media: el.attrs.media === "video" ? "video" : "audio",
      result: group ? undefined : (result ?? "missed"),
      duration: group ? undefined : Math.max(0, Math.min(86400, Number(el.attrs.duration) || 0)),
      group: group || undefined,
    },
  } as Content;
}

function envelopeEl(env: Envelope, op: string) {
  return xml(
    "encrypted",
    { xmlns: NS.e2e, op },
    xml(
      "header",
      { sid: env.sid },
      env.keys.map((k) => xml("key", { jid: k.jid, rid: k.rid, prekey: k.prekey ? "true" : undefined }, k.data)),
      xml("iv", {}, env.iv),
    ),
    xml("payload", {}, env.payload),
  );
}

function parseEnvelope(el: Element): Envelope | null {
  const h = el.getChild("header");
  const sid = num(h?.attrs.sid);
  const iv = h?.getChildText("iv");
  const payload = el.getChildText("payload");
  if (!h || !sid || !iv || !payload) return null;
  const keys = h.getChildren("key").flatMap((k) => {
    const rid = num(k.attrs.rid);
    return rid && k.attrs.jid ? [{ jid: bare(k.attrs.jid), rid, prekey: k.attrs.prekey === "true", data: k.getText() }] : [];
  });
  return { sid, iv, payload, keys };
}

/** What goes inside the encrypted payload for a message. */
function contentPayload(m: Msg) {
  return {
    op: "msg",
    v: 1,
    sent: m.ts, // when it was sent: recipients order by it (sentTime)
    kind: m.kind,
    body: m.body,
    media: m.media ? { ...m.media, blob: undefined } : undefined,
    replyTo: m.replyTo
      ? { id: m.replyTo.id, from: m.replyTo.from, ...(m.replyTo.id.startsWith("status:") || m.replyTo.chat ? { kind: m.replyTo.kind, text: m.replyTo.text, chat: m.replyTo.chat } : {}) }
      : undefined,
    preview: m.preview,
    forwarded: m.forwarded,
    mentions: m.mentions,
    contact: m.contact,
    location: m.location,
    sticker: m.sticker,
    statusRef: m.statusRef,
    // Answers travel separately (vote / rsvp ops).
    poll: m.poll ? { question: m.poll.question, options: m.poll.options, multi: m.poll.multi } : undefined,
    event: m.event ? { name: m.event.name, description: m.event.description, start: m.event.start, end: m.event.end, place: m.event.place, call: m.event.call } : undefined,
    ttl: m.ttl,
    viewOnce: m.viewOnce,
    bcast: m.bcast,
  };
}

const str = (v: unknown, max: number) => (typeof v === "string" ? v.slice(0, max) : "");
const numv = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : undefined);
const b64s = (v: unknown) => (typeof v === "string" && /^[A-Za-z0-9+/=]{1,200}$/.test(v) ? v : undefined);

function sanitizePreview(v: unknown): LinkPreview | undefined {
  const p = v as Record<string, unknown> | undefined;
  const url = safeURL(typeof p?.url === "string" ? p.url : undefined);
  if (!p || !url || typeof p.title !== "string") return undefined;
  return {
    url,
    title: str(p.title, 200),
    description: str(p.description, 300) || undefined,
    site_name: str(p.site_name, 100) || undefined,
    thumb: safeThumb(typeof p.thumb === "string" ? p.thumb : undefined),
    large: p.large === true || undefined,
  };
}

export const POLL_MAX_OPTIONS = 12;

function sanitizePoll(v: unknown): Poll | undefined {
  const p = v as Record<string, unknown> | undefined;
  const question = str(p?.question, 300).trim();
  if (!p || !question || !Array.isArray(p.options)) return undefined;
  const seen = new Set<string>();
  const options: Poll["options"] = [];
  for (const o of p.options.slice(0, POLL_MAX_OPTIONS) as Record<string, unknown>[]) {
    const id = str(o?.id, 16);
    const text = str(o?.text, 100).trim();
    if (id && text && !seen.has(id)) (seen.add(id), options.push({ id, text }));
  }
  return options.length >= 2 ? { question, options, multi: p.multi === true } : undefined;
}

function sanitizeEvent(v: unknown): ChatEvent | undefined {
  const e = v as Record<string, unknown> | undefined;
  const name = str(e?.name, 200).trim();
  const start = numv(e?.start);
  if (!e || !name || !start) return undefined;
  const end = numv(e.end);
  return {
    name,
    description: str(e.description, 2048).trim() || undefined,
    start,
    end: end && end > start ? end : undefined,
    place: str(e.place, 300).trim() || undefined,
    call: e.call === "voice" || e.call === "video" ? e.call : undefined,
  };
}

function sanitizeLoc(v: unknown) {
  const l = v as Record<string, unknown> | undefined;
  const lat = numv(l?.lat);
  const lon = numv(l?.lon);
  if (lat === undefined || lon === undefined || Math.abs(lat) > 90 || Math.abs(lon) > 180) return undefined;
  const acc = numv(l?.accuracy);
  return { lat, lon, accuracy: acc && acc > 0 ? acc : undefined, name: str(l?.name, 200) || undefined };
}

/** Decrypted payloads are validated as strictly as plaintext stanzas. */
function sanitizeContent(p: Record<string, unknown>): Content {
  const out: Content = { kind: "text", body: str(p.body, 65_536) };
  const kind = p.kind as Kind;
  const md = p.media as Record<string, unknown> | undefined;
  const url = mediaURL(typeof md?.url === "string" ? md.url : undefined);
  if (md && url && KINDS.includes(kind)) {
    out.kind = kind;
    out.media = {
      url,
      mime: str(md.mime, 100) || "application/octet-stream",
      size: numv(md.size) ?? 0,
      name: str(md.name, 200) || undefined,
      width: numv(md.width),
      height: numv(md.height),
      duration: numv(md.duration),
      pages: numv(md.pages) && Math.min(100_000, Math.floor(numv(md.pages)!)),
      gif: kind === "video" && md.gif === true ? true : undefined,
      note: kind === "video" && md.note === true ? true : undefined,
      thumb: safeThumb(typeof md.thumb === "string" ? md.thumb : undefined),
      waveform: Array.isArray(md.waveform) ? md.waveform.slice(0, 128).map((x) => Math.max(0, Math.min(100, Number(x) || 0))) : undefined,
      key: b64s(md.key),
      iv: b64s(md.iv),
      sha256: b64s(md.sha256),
    };
  }
  const r = p.replyTo as Record<string, unknown> | undefined;
  if (r && typeof r.id === "string") {
    out.replyTo = { id: r.id.slice(0, 128), from: bare(str(r.from, 300)) };
    // Replies to a status, and private replies to a group message, carry what
    // the quoted message was (it isn't in this chat).
    const fromGroup = typeof r.chat === "string" && /^g[0-9a-f]+@conference\./.test(r.chat);
    if (r.id.startsWith("status:") || fromGroup) Object.assign(out.replyTo, { kind: KINDS.includes(r.kind as Kind) || r.kind === "text" ? r.kind : "text", text: str(r.text, 200), chat: fromGroup ? bare(r.chat as string) : undefined });
  }
  if (typeof p.bcast === "string" && /^b-[A-Za-z0-9_-]{8,64}@broadcast$/.test(p.bcast)) out.bcast = p.bcast;
  out.preview = sanitizePreview(p.preview);
  if (p.forwarded === true) out.forwarded = true;
  if (Array.isArray(p.mentions)) {
    const ms = p.mentions
      .map((x) => x as Record<string, unknown>)
      .filter((x) => typeof x.jid === "string" && numv(x.begin) !== undefined && numv(x.end) !== undefined)
      .map((x) => ({ jid: bare(x.jid as string), begin: x.begin as number, end: x.end as number }))
      .filter((x) => x.begin >= 0 && x.end > x.begin && x.end <= out.body.length)
      .slice(0, 50);
    if (ms.length) out.mentions = ms;
  }
  const c = p.contact as Record<string, unknown> | undefined;
  if (kind === "contact" && c && /^\+\d{8,15}$/.test(str(c.phone, 20))) {
    out.kind = "contact";
    out.contact = { name: str(c.name, 100) || str(c.phone, 20), phone: str(c.phone, 20), jid: typeof c.jid === "string" && /^\d+@/.test(c.jid) ? bare(c.jid) : undefined };
  }
  const ttl = numv(p.ttl);
  if (ttl && ALLOWED_TTLS.includes(ttl)) out.ttl = ttl;
  if (p.viewOnce === true && (out.kind === "image" || out.kind === "video")) out.viewOnce = true;
  if (out.kind === "sticker") {
    const st = p.sticker as Record<string, unknown> | undefined;
    const pack = typeof st?.pack === "string" && /^[0-9a-f]{32}$/.test(st.pack) ? st.pack : undefined;
    const key = typeof st?.key === "string" && /^[0-9a-f]{64}$/.test(st.key) ? st.key : undefined;
    out.sticker = { pack: pack && key ? pack : undefined, key: pack && key ? key : undefined, id: numv(st?.id), emoji: str(st?.emoji, 16) || undefined };
    out.body = "";
  }
  const poll = sanitizePoll(p.poll);
  if (kind === "poll" && poll) {
    out.kind = "poll";
    out.poll = poll;
  }
  const ev = sanitizeEvent(p.event);
  if (kind === "event" && ev) {
    out.kind = "event";
    out.event = ev;
  }
  const sr = p.statusRef as Record<string, unknown> | undefined;
  if (out.kind === "text" && sr && typeof sr.id === "string" && /^[A-Za-z0-9_-]{8,64}$/.test(sr.id) && ["text", "image", "video"].includes(sr.kind as string))
    out.statusRef = { id: sr.id, kind: sr.kind as "text" | "image" | "video", text: str(sr.text, 200) || undefined };
  const loc = sanitizeLoc(p.location);
  if (kind === "location" && loc) {
    out.kind = "location";
    const live = (p.location as Record<string, unknown>).live as Record<string, unknown> | undefined;
    const until = numv(live?.until);
    out.location = { ...loc, live: until ? { until: Math.min(until, Date.now() + 9 * 3_600_000), updatedAt: 0, stopped: live?.stopped === true || undefined } : undefined };
  }
  return out;
}

function geolocEl(l: { lat: number; lon: number; accuracy?: number; name?: string }) {
  return xml(
    "geoloc",
    { xmlns: NS.geoloc },
    xml("lat", {}, l.lat.toFixed(6)),
    xml("lon", {}, l.lon.toFixed(6)),
    l.accuracy ? xml("accuracy", {}, String(Math.round(l.accuracy))) : null,
    l.name ? xml("text", {}, l.name) : null,
  );
}

function parseGeoloc(g?: Element): { lat: number; lon: number; accuracy?: number; name?: string } | undefined {
  if (!g) return undefined;
  const lat = Number(g.getChildText("lat"));
  const lon = Number(g.getChildText("lon"));
  if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) return undefined;
  const acc = Number(g.getChildText("accuracy"));
  return { lat, lon, accuracy: Number.isFinite(acc) && acc > 0 ? acc : undefined, name: g.getChildText("text")?.slice(0, 200) || undefined };
}

function urlB64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const bin = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

const msgType = (chat: string) => (S.isGroup(chat) ? "groupchat" : "chat");

function blankGroup(jid: string): Group {
  return { jid, name: "Group", description: "", avatar_url: null, only_admins_send: false, admins_send_since: null, only_admins_edit: false, created_at: 0, updated_at: 0, members: [], my_role: "" };
}

function previewEl(p: LinkPreview) {
  return xml(
    "preview",
    { xmlns: NS.preview, url: p.url, title: p.title, description: p.description, site: p.site_name },
    p.thumb ? xml("thumb", {}, p.thumb) : null,
  );
}

const KINDS: Kind[] = ["image", "video", "audio", "voice", "file", "sticker"];
const num = (v?: string) => (v && Number.isFinite(Number(v)) ? Number(v) : undefined);
/** Links (previews) may point anywhere on the web... */
const safeURL = (u?: string) => (u && /^https?:\/\//.test(u) ? u : undefined);
/** ...but media is only loaded from our own media store, so a message can't
 * make the recipient's browser fetch third-party URLs (tracking, IP leaks). */
function mediaURL(u?: string): string | undefined {
  if (!u) return undefined;
  try {
    const parsed = new URL(u, baseURL());
    if (/^\/media\/[0-9a-f]{32}$/.test(parsed.pathname) && parsed.origin === baseOrigin()) return parsed.pathname;
    // Media behind a CDN (XMM_MEDIA_BASE_URL): https://cdn.example/<id>
    const cdn = S.mediaBase.value;
    if (cdn && u.startsWith(cdn + "/") && /^[0-9a-f]{32}$/.test(u.slice(cdn.length + 1))) return u;
  } catch {
    /* invalid */
  }
  return undefined;
}
const safeThumb = (t?: string | null) => (t && t.startsWith("data:image/") && t.length < 60_000 ? t : undefined);

function parsePreview(m: Element): LinkPreview | undefined {
  const p = m.getChild("preview", NS.preview);
  const url = safeURL(p?.attrs.url);
  if (!p || !url || !p.attrs.title) return undefined;
  return {
    url,
    title: p.attrs.title.slice(0, 200),
    description: p.attrs.description?.slice(0, 300),
    site_name: p.attrs.site,
    thumb: safeThumb(p.getChildText("thumb")),
  };
}

/** Everything a stored message needs from the stanza, validated. */
type Content = Pick<Msg, "kind" | "body" | "media" | "replyTo" | "preview" | "forwarded" | "mentions" | "contact" | "location" | "undecryptable" | "insecure" | "ttl" | "viewOnce" | "call" | "bcast" | "poll" | "event" | "sticker" | "statusRef">;

function parseContent(m: Element, body: string): Content {
  const out: Content = { kind: "text", body };
  const el = m.getChild("media", NS.media);
  const url = mediaURL(el?.attrs.url);
  if (el && url) {
    const kind = KINDS.includes(el.attrs.kind as Kind) ? (el.attrs.kind as Kind) : "file";
    const wf = el.getChildText("waveform");
    out.kind = kind;
    out.media = {
      url,
      mime: el.attrs.mime ?? "application/octet-stream",
      size: num(el.attrs.size) ?? 0,
      name: el.attrs.name?.slice(0, 200),
      width: num(el.attrs.width),
      height: num(el.attrs.height),
      duration: num(el.attrs.duration),
      thumb: safeThumb(el.getChildText("thumb")),
      waveform: wf ? wf.split(",").slice(0, 128).map((x) => Math.max(0, Math.min(100, Number(x) || 0))) : undefined,
    };
    if (body === el.attrs.url) out.body = ""; // no caption
  }
  const reply = m.getChild("reply", NS.reply);
  if (reply?.attrs.id) out.replyTo = { id: reply.attrs.id, from: bare(reply.attrs.to) };
  out.preview = parsePreview(m);
  if (m.getChild("fwd", NS.fwd)) out.forwarded = true;
  const mentions = m
    .getChildren("reference", NS.reference)
    .filter((r) => r.attrs.type === "mention" && r.attrs.uri?.startsWith("xmpp:"))
    .map((r) => ({ jid: bare(r.attrs.uri!.slice(5)), begin: num(r.attrs.begin) ?? -1, end: num(r.attrs.end) ?? -1 }))
    .filter((x) => x.begin >= 0 && x.end > x.begin && x.end <= body.length)
    .slice(0, 50);
  if (mentions.length) out.mentions = mentions;

  const c = m.getChild("contact", NS.contact);
  if (c && /^\+\d{8,15}$/.test(c.attrs.phone ?? "")) {
    const jid = c.attrs.jid && /^\d+@/.test(c.attrs.jid) ? bare(c.attrs.jid) : undefined;
    out.kind = "contact";
    out.contact = { name: (c.attrs.name || c.attrs.phone!).slice(0, 100), phone: c.attrs.phone!, jid };
    out.body = "";
  }
  const loc = parseGeoloc(m.getChild("geoloc", NS.geoloc));
  if (loc && out.kind === "text") {
    out.kind = "location";
    const until = num(m.getChild("live", NS.live)?.attrs.until);
    out.location = { ...loc, live: until ? { until: Math.min(until, Date.now() + 9 * 3_600_000), updatedAt: 0 } : undefined };
    out.body = body.startsWith("geo:") ? "" : body;
  }
  return out;
}

/** What the native app needs to show a message notification (see native.ts). */
export type NativeNotice = { chat: string; id: string; title: string; body: string; sender: string; chatName: string; group: boolean; avatar: string | null; ts: number };

/** A notification icon: the avatar cropped to a circle (the product logo without one). */
async function circleIcon(url: string | null): Promise<string> {
  if (!url) return "/icon-192.png";
  try {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = url;
    await img.decode();
    const size = 192;
    const c = document.createElement("canvas");
    c.width = c.height = size;
    const g = c.getContext("2d")!;
    g.beginPath();
    g.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    g.clip();
    const side = Math.min(img.naturalWidth, img.naturalHeight);
    g.drawImage(img, (img.naturalWidth - side) / 2, (img.naturalHeight - side) / 2, side, side, 0, 0, size, size);
    return c.toDataURL("image/png");
  } catch {
    return url;
  }
}
